import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import SaveIcon from "@mui/icons-material/Save";
import PrintIcon from "@mui/icons-material/Print";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import JSZip from "jszip";
import fichaTemplateDocx from "./assets/rptFichaAfiliacionVacia.docx";
import injupempGobiernoLogo from "../../img/LogoInjupemp2026.png";
import { ContextSession } from "../../services/ContextSession";
import {
  apiRequest,
  buildActualizarPayload,
  consultarRnpIdentidad,
  getUltimaFotoPersona,
} from "./enrolamientoApi";
import {
  formatIdentificacionVisual,
  formatTelefonoVisual,
  stripIdentificacionVisual,
} from "./identificacionUtils";

const fallbackTipoDocumentoOptions = ["IDENTIDAD", "PASAPORTE", "RTN"];
const genericYesNo = ["Si", "No"];
const fallbackReferenciaTipoOptions = [
  "COMERCIALES / BANCARIAS",
  "LABORALES",
  "PERSONALES",
];
const fallbackPadecimientoOptions = [
  "COLON",
  "DIABETES",
  "HIPERTENSION",
  "ASMA",
  "CARDIOPATIA",
  "ARTRITIS",
];
const fallbackPaisNacimientoOptions = [
  "HONDURAS",
  "GUATEMALA",
  "EL SALVADOR",
  "NICARAGUA",
];
const fallbackParentescoOptions = [
  "PADRE",
  "MADRE",
  "HIJO(A)",
  "CONYUGE",
  "HERMANO(A)",
  "NIETO(A)",
  "OTRO",
];

const stepTitles = [
  { title: "Búsqueda", subtitle: "Buscar participante" },
  { title: "Datos personales", subtitle: "Información personal y residencia" },
  { title: "Fotografía", subtitle: "Captura de foto frontal" },
  { title: "Afiliación", subtitle: "Información de afiliación" },
  {
    title: "Revisión de la información",
    subtitle: "Verifique y confirme la información capturada",
  },
];

const baseAfiliacionTabs = [
  "Información Laboral",
  "Patrimonio",
  "Referencias",
  "Beneficiarios",
];

const fallbackPpeParentescoOptions = [
  "CONYUGE",
  "HIJO(A)",
  "PADRE",
  "MADRE",
  "HERMANO(A)",
  "NUERA/YERNO",
  "ABUELO(A)",
  "OTRO",
];

const fallbackPpeNacionalidadOptions = [
  "HONDUREÑA",
  "GUATEMALTECA",
  "SALVADOREÑA",
  "NICARAGUENSE",
];
const _ONLY_LETTERS = /[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g;
const _ONLY_LETTERS_NUMS = /[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\s]/g;
const _ONLY_DIGITS = /\D/g;
const _NAME_FIELDS = [
  "primerNombre",
  "segundoNombre",
  "primerApellido",
  "segundoApellido",
  "nombreConyuge",
];
const _BENEFICIARIO_NAME_FIELDS = new Set([
  "primerNombre",
  "segundoNombre",
  "primerApellido",
  "segundoApellido",
]);
const _NUM_FIELDS = [
  "telefono",
  "movil",
  "rtn",
  "identificacionConyuge",
  "telefonoTrabajo",
];
const _UPPER_FIELDS = [
  "patrono",
  "lugarNacimiento",
  "coloniaResidencia",
  "direccionCompleta",
  "calle",
  "numeroCasa",
  "referenciaDireccion",
  "profesion",
  "actividadEconomicaCnbs",
  "profesionCnbs",
  "direccionTrabajo",
  "numeroCasaTrabajo",
  "referenciaTrabajo",
  "areaTrabajo",
  "descOtrosInmuebles",
  "descOtrosIngresos",
];
const _EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_TIEMPO_RESIDIR = 150;
const normalizeBeneficiarioNameInput = (value) => {
  const raw = `${value ?? ""}`.replace(_ONLY_LETTERS, "").toUpperCase();
  const collapsed = raw.replace(/\s+/g, " ").replace(/^\s+/, "");
  if (!collapsed) return "";
  return /\s$/.test(raw) ? `${collapsed.trimEnd()} ` : collapsed.trim();
};
const normalizeBeneficiarioNameValue = (value) =>
  normalizeBeneficiarioNameInput(value).trim();

const OFICINA_NOMBRES = {
  1: "Tegucigalpa",
  2: "San Pedro Sula",
  3: "La Ceiba",
  4: "Choluteca",
  5: "Santa Rosa de Copán",
  6: "Santa Bárbara",
  7: "Juticalpa",
};

const resolverNombreOficina = (oficinaSesion) => {
  const raw = String(oficinaSesion ?? "").trim();
  if (!raw) return OFICINA_NOMBRES[1];
  return OFICINA_NOMBRES[raw] || OFICINA_NOMBRES[Number(raw)] || raw;
};

const emptyCatalogos = {
  documentoTipos: [],
  estadosCiviles: [],
  sexos: [],
  nacionalidades: [],
  nivelesEducativos: [],
  perfilesLaft: [],
  padecimientos: [],
  profesiones: [],
  actividadesEconomicasCnbs: [],
  profesionesCnbs: [],
  viviendaTipos: [],
  ingresosAproximados: [],
  girosEntidad: [],
  cargos: [],
  departamentos: [],
  municipios: [],
  colonias: [],
  parentescos: [],
  tipoClientes: [],
  referenciaTipos: [],
};

const tableHeaderStyle = {
  backgroundColor: "#19498c",
  color: "#ffffff",
  fontWeight: 700,
};

const sectionPaper = {
  p: 2.2,
  borderRadius: 3,
  border: "1px solid #dbe6f6",
  boxShadow: "0 8px 24px rgba(25, 73, 140, 0.08)",
};

function SectionTitle({ children }) {
  return (
    <Typography sx={{ fontWeight: 700, color: "#203040", mb: 1.5 }}>
      {children}
    </Typography>
  );
}

export default function RegistrarAfiliacion() {
  const sessi = useContext(ContextSession);
  const authToken = sessi?.sessionData?.token || "";
  const [catalogos, setCatalogos] = useState({ ...emptyCatalogos });
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);
  const [step, setStep] = useState(0);
  const [tab, setTab] = useState(0);
  const [noCorreo, setNoCorreo] = useState(false);
  const [afiliadoInjupemp, setAfiliadoInjupemp] = useState(true);
  const [searchError, setSearchError] = useState("");
  const [searchingPersona, setSearchingPersona] = useState(false);
  const [openPersonaExistenteModal, setOpenPersonaExistenteModal] =
    useState(false);
  const [personaExistente, setPersonaExistente] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [cameraOpen, setCameraOpen] = useState(false);
  const [photoDataUrl, setPhotoDataUrl] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [photoBlocked, setPhotoBlocked] = useState(false);
  const [justificacionFoto, setJustificacionFoto] = useState("");
  const [summaryPhotoOpen, setSummaryPhotoOpen] = useState(false);
  const [prevSummaryPhoto, setPrevSummaryPhoto] = useState("");
  const [openCargoModal, setOpenCargoModal] = useState(false);
  const [otrosCargos, setOtrosCargos] = useState([]);
  const [cargoModalError, setCargoModalError] = useState({});
  const [cargoForm, setCargoForm] = useState({
    empleadorPatrono: "",
    cargoDesempenado: "",
    actividadGiro: "",
  });
  const [editingCargoId, setEditingCargoId] = useState(null);
  const [editingPpeFamiliarId, setEditingPpeFamiliarId] = useState(null);
  const [editingPpeEmpresaId, setEditingPpeEmpresaId] = useState(null);
  const [editingBeneficiarioId, setEditingBeneficiarioId] = useState(null);
  const [openReferenciaModal, setOpenReferenciaModal] = useState(false);
  const [editingReferenciaId, setEditingReferenciaId] = useState(null);
  const [referencias, setReferencias] = useState([]);
  const [referenciaModalError, setReferenciaModalError] = useState({});
  const [referenciaRequirementError, setReferenciaRequirementError] =
    useState("");
  const [ppeFamiliarRequirementError, setPpeFamiliarRequirementError] =
    useState("");
  const [referenciaForm, setReferenciaForm] = useState({
    tipoReferencia: "COMERCIALES / BANCARIAS",
    nombreCompleto: "",
    telefono: "",
    direccion: "",
  });
  const [openPadecimientoModal, setOpenPadecimientoModal] = useState(false);
  const [padecimientosList, setPadecimientosList] = useState([]);
  const [padecimientoModalError, setPadecimientoModalError] = useState("");
  const [padecimientoForm, setPadecimientoForm] = useState({
    nombre: "",
  });
  const [openBeneficiarioModal, setOpenBeneficiarioModal] = useState(false);
  const [beneficiarios, setBeneficiarios] = useState([]);
  const [beneficiarioModalError, setBeneficiarioModalError] = useState({});
  const [beneficiariosRequirementError, setBeneficiariosRequirementError] =
    useState("");
  const [fichaAdjunta, setFichaAdjunta] = useState(null);
  const [fichaAdjuntaPep, setFichaAdjuntaPep] = useState(null);
  const [saveFichaError, setSaveFichaError] = useState("");
  const [saveFichaSuccess, setSaveFichaSuccess] = useState("");
  const [savingRegistro, setSavingRegistro] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [savedPersonaName, setSavedPersonaName] = useState("");
  const [beneficiarioForm, setBeneficiarioForm] = useState({
    primerNombre: "",
    segundoNombre: "",
    primerApellido: "",
    segundoApellido: "",
    numeroDocumento: "",
    parentesco: "",
    esLegal: false,
    porcentaje: "",
  });
  const [openPpeFamiliarModal, setOpenPpeFamiliarModal] = useState(false);
  const [openPpeEmpresaModal, setOpenPpeEmpresaModal] = useState(false);
  const [ppeFamiliares, setPpeFamiliares] = useState([]);
  const [ppeEmpresas, setPpeEmpresas] = useState([]);
  const [ppeFamiliarError, setPpeFamiliarError] = useState({});
  const [ppeEmpresaError, setPpeEmpresaError] = useState({});
  const [ppeFamiliarForm, setPpeFamiliarForm] = useState({
    primerNombre: "",
    segundoNombre: "",
    primerApellido: "",
    segundoApellido: "",
    parentesco: "",
    nacionalidad: "",
  });
  const [ppeEmpresaForm, setPpeEmpresaForm] = useState({
    razonSocial: "",
    rtn: "",
    giroNegocio: "",
    pais: "",
  });
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraStream, setCameraStream] = useState(null);
  const enforceStepValidation = true;
  const totalPorcentajeLegal = useMemo(
    () =>
      beneficiarios
        .filter((item) => item.tipo === "Legal")
        .reduce((acc, item) => acc + Number(item.porcentaje || 0), 0),
    [beneficiarios],
  );
  const porcentajeDisponibleLegal = Math.max(0, 100 - totalPorcentajeLegal);
  const totalBeneficiariosPotenciales = useMemo(
    () => beneficiarios.filter((item) => item.tipo === "Potencial").length,
    [beneficiarios],
  );
  const totalBeneficiariosLegales = useMemo(
    () => beneficiarios.filter((item) => item.tipo === "Legal").length,
    [beneficiarios],
  );

  const [form, setForm] = useState({
    tipoDocumento: "IDENTIDAD",
    identificacion: "",
    primerNombre: "",
    segundoNombre: "",
    primerApellido: "",
    segundoApellido: "",
    genero: "",
    paisNacimiento: "HONDURAS",
    nacionalidad: "HONDUREÑA",
    fechaNacimiento: "",
    lugarNacimiento: "",
    estadoCivil: "",
    departamentoResidencia: "",
    municipioResidencia: "",
    coloniaResidencia: "",
    direccionCompleta: "",
    calle: "",
    numeroCasa: "",
    referenciaDireccion: "",
    tiempoResidir: 0,
    fechaDesdeReside: "",
    telefono: "",
    movil: "",
    correo: "",
    tipoCliente: "",
    rtn: "",
    profesion: "",
    actividadEconomicaCnbs: "",
    profesionCnbs: "",
    nivelEducativo: "",
    nombreConyuge: "",
    tipoDocumentoConyuge: "",
    identificacionConyuge: "",
    generoConyuge: "",
    fechaNacimientoConyuge: "",
    patrono: "",
    departamentoTrabajo: "",
    municipioTrabajo: "",
    coloniaTrabajo: "",
    direccionTrabajo: "",
    numeroCasaTrabajo: "",
    referenciaTrabajo: "",
    fechaIngresoInstitucion: "",
    antiguedadLaboral: "",
    ingresoAproxRango: "",
    ingresoAproxMonto: 0,
    giroEntidad: "",
    cargoDesempenado: "",
    areaTrabajo: "",
    telefonoTrabajo: "",
    tipoVivienda: "",
    poseeVehiculo: "No",
    numeroVehiculos: 0,
    poseeInmuebles: "No",
    descOtrosInmuebles: "",
    otrosIngresos: "No",
    descOtrosIngresos: "",
    montoOtrosIngresos: 0,
    dependientesEconomicos: 0,
    esPep: false,
    enviarRevision: false,
    noDesignacionBeneficiarios: false,
    perfilLaft: "A",
  });
  const afiliacionTabs = useMemo(() => {
    if (!form.esPep) return baseAfiliacionTabs;
    const beneficiariosIndex = baseAfiliacionTabs.indexOf("Beneficiarios");
    if (beneficiariosIndex < 0) {
      return [...baseAfiliacionTabs, "Persona Políticamente Expuesta"];
    }
    return [
      ...baseAfiliacionTabs.slice(0, beneficiariosIndex),
      "Persona Políticamente Expuesta",
      ...baseAfiliacionTabs.slice(beneficiariosIndex),
    ];
  }, [form.esPep]);
  const currentTabName = afiliacionTabs[tab] || afiliacionTabs[0];

  // Giro de la Entidad predeterminado: ID=1 "Administración Pública"
  const giroEntidadDefault = useMemo(() => {
    const found = (catalogos.girosEntidad || []).find(
      (g) => String(g?.GiroEntidadId ?? "").trim() === "1",
    );
    return found ? String(found.Descripcion || "").trim() : "";
  }, [catalogos.girosEntidad]);

  // Predeterminar giroEntidad cuando carguen los catálogos (solo si está vacío)
  useEffect(() => {
    if (giroEntidadDefault && !form.giroEntidad) {
      setForm((prev) => ({ ...prev, giroEntidad: giroEntidadDefault }));
    }
  }, [giroEntidadDefault]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let active = true;
    const loadCatalogos = async () => {
      if (!authToken) return;
      try {
        setLoadingCatalogos(true);
        const data = await apiRequest("/Enrolamiento/catalogos/", {
          authToken,
        });
        if (!active) return;
        const c = data?.catalogos || {};
        setCatalogos({
          documentoTipos: c?.documento_tipos || [],
          estadosCiviles: c?.estados_civiles || [],
          sexos: c?.sexos || [],
          nacionalidades: c?.nacionalidades || [],
          nivelesEducativos: c?.niveles_educativos || [],
          perfilesLaft: c?.perfiles_laft || [],
          padecimientos: c?.padecimientos || [],
          profesiones: c?.profesiones || [],
          actividadesEconomicasCnbs: c?.actividades_economicas_cnbs || [],
          profesionesCnbs: c?.profesiones_cnbs || [],
          viviendaTipos: c?.vivienda_tipos || [],
          ingresosAproximados: c?.ingresos_aproximados || [],
          girosEntidad: c?.giros_entidad || [],
          cargos: c?.cargos || [],
          departamentos: c?.departamentos || [],
          municipios: c?.municipios || [],
          colonias: c?.colonias || [],
          parentescos: c?.parentescos || [],
          tipoClientes: c?.tipo_cliente_solicitantes || [],
          referenciaTipos: c?.referencia_tipos || [],
        });
      } catch (_err) {
        if (!active) return;
        setCatalogos({ ...emptyCatalogos });
      } finally {
        if (active) setLoadingCatalogos(false);
      }
    };
    loadCatalogos();
    return () => {
      active = false;
    };
  }, [authToken]);

  const documentoTipoOptions = useMemo(
    () =>
      catalogos.documentoTipos?.length
        ? catalogos.documentoTipos
            .map((item) => item?.Descripcion)
            .filter(Boolean)
        : fallbackTipoDocumentoOptions,
    [catalogos.documentoTipos],
  );
  const referenciaTipoOptions = useMemo(
    () =>
      catalogos.referenciaTipos?.length
        ? catalogos.referenciaTipos.map((item) => item?.Nombre).filter(Boolean)
        : fallbackReferenciaTipoOptions,
    [catalogos.referenciaTipos],
  );
  const padecimientoOptions = useMemo(
    () =>
      catalogos.padecimientos?.length
        ? catalogos.padecimientos
            .map((item) => item?.Descripcion)
            .filter(Boolean)
        : fallbackPadecimientoOptions,
    [catalogos.padecimientos],
  );
  const parentescoOptions = useMemo(
    () =>
      catalogos.parentescos?.length
        ? catalogos.parentescos.map((item) => item?.Descripcion).filter(Boolean)
        : fallbackParentescoOptions,
    [catalogos.parentescos],
  );
  const ppeParentescoOptions = useMemo(
    () =>
      parentescoOptions?.length
        ? parentescoOptions
        : fallbackPpeParentescoOptions,
    [parentescoOptions],
  );
  const getPaisDescripcion = (item) => String(item?.Descripcion ?? "").trim();
  const getNacionalidadGentilicio = (item) => {
    const gentilicio = String(
      item?.GentilicioDisplay ?? item?.Gentilicio ?? "",
    ).trim();
    return gentilicio || getPaisDescripcion(item);
  };
  const paisNacimientoOptions = useMemo(() => {
    const values = (catalogos.nacionalidades || [])
      .map((item) => getPaisDescripcion(item))
      .filter(Boolean);
    return Array.from(
      new Set(values.length ? values : fallbackPaisNacimientoOptions),
    );
  }, [catalogos.nacionalidades]);
  const nacionalidadOptions = useMemo(() => {
    const values = (catalogos.nacionalidades || [])
      .map((item) => getNacionalidadGentilicio(item))
      .filter(Boolean);
    return Array.from(
      new Set(values.length ? values : fallbackPpeNacionalidadOptions),
    );
  }, [catalogos.nacionalidades]);
  const ppeNacionalidadOptions = useMemo(
    () =>
      nacionalidadOptions?.length
        ? nacionalidadOptions
        : fallbackPpeNacionalidadOptions,
    [nacionalidadOptions],
  );
  const sexoOptions = useMemo(() => {
    const fromCatalog = (catalogos.sexos || [])
      .map((item) => {
        const descripcion = String(item?.Descripcion ?? "").trim();
        const baseValue = String(
          item?.SexoId ?? item?.Codigo ?? descripcion,
        ).trim();
        const normalized = baseValue.toUpperCase();
        let value = "";
        if (normalized === "M" || normalized.includes("MASC")) value = "M";
        if (!value && (normalized === "F" || normalized.includes("FEM"))) {
          value = "F";
        }
        if (!value) return null;
        return {
          value,
          label: descripcion || (value === "M" ? "Masculino" : "Femenino"),
        };
      })
      .filter(Boolean);

    const unique = new Map();
    (fromCatalog.length
      ? fromCatalog
      : [
          { value: "M", label: "Masculino" },
          { value: "F", label: "Femenino" },
        ]
    ).forEach((item) => {
      if (!unique.has(item.value)) unique.set(item.value, item);
    });
    return Array.from(unique.values());
  }, [catalogos.sexos]);
  const estadoCivilOptions = useMemo(() => {
    const normalizeEstadoCivil = (rawValue) => {
      const normalized = String(rawValue ?? "")
        .trim()
        .toUpperCase();
      if (!normalized) return "";
      if (normalized === "S" || normalized.includes("SOLTER")) return "SOLTERO";
      if (normalized === "C" || normalized.includes("CASAD")) return "CASADO";
      if (
        normalized === "U" ||
        normalized === "UL" ||
        normalized.includes("UNION") ||
        normalized.includes("UNIÓN")
      ) {
        return "UNION";
      }
      return normalized;
    };

    const fromCatalog = (catalogos.estadosCiviles || [])
      .map((item) => {
        const descripcion = String(item?.Descripcion ?? "").trim();
        const estadoCivilId =
          item?.EstadoCivilId === undefined || item?.EstadoCivilId === null
            ? ""
            : String(item.EstadoCivilId).trim();
        const value = estadoCivilId || normalizeEstadoCivil(descripcion);
        if (!value) return null;
        return {
          value,
          label: descripcion || value,
        };
      })
      .filter(Boolean);

    const fallback = [
      { value: "S", label: "Soltero" },
      { value: "C", label: "Casado" },
      { value: "U", label: "Unión de hecho" },
    ];

    const unique = new Map();
    (fromCatalog.length ? fromCatalog : fallback).forEach((item) => {
      if (!unique.has(item.value)) unique.set(item.value, item);
    });
    return Array.from(unique.values());
  }, [catalogos.estadosCiviles]);
  const getEstadoCivilLabel = (value) => {
    const current = asText(value);
    if (!current) return "";
    const found = estadoCivilOptions.find(
      (option) => asText(option.value) === current,
    );
    return found?.label || current;
  };
  const hondurasCatalogItem = useMemo(
    () =>
      (catalogos.nacionalidades || []).find((item) => {
        const id = String(item?.NacionalidadId ?? "")
          .trim()
          .toUpperCase();
        const descripcion = String(item?.Descripcion ?? "")
          .trim()
          .toUpperCase();
        return id === "HND" || descripcion === "HONDURAS";
      }),
    [catalogos.nacionalidades],
  );
  useEffect(() => {
    if (!hondurasCatalogItem) return;
    const paisDefault = getPaisDescripcion(hondurasCatalogItem);
    const nacionalidadDefault = getNacionalidadGentilicio(hondurasCatalogItem);

    setForm((prev) => {
      const next = { ...prev };
      let changed = false;

      const paisActual = String(prev?.paisNacimiento ?? "").trim();
      if (
        (!paisActual || paisActual.toUpperCase() === "HONDURAS") &&
        paisDefault &&
        paisActual !== paisDefault
      ) {
        next.paisNacimiento = paisDefault;
        changed = true;
      }

      const nacionalidadActual = String(prev?.nacionalidad ?? "").trim();
      if (
        (!nacionalidadActual ||
          nacionalidadActual.toUpperCase().startsWith("HONDUR")) &&
        nacionalidadDefault &&
        nacionalidadActual !== nacionalidadDefault
      ) {
        next.nacionalidad = nacionalidadDefault;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [hondurasCatalogItem]);
  const perfilLaftOptions = useMemo(
    () =>
      catalogos.perfilesLaft?.length
        ? catalogos.perfilesLaft
            .map((item) => item?.Descripcion)
            .filter(Boolean)
        : ["A", "B", "C", "D"],
    [catalogos.perfilesLaft],
  );
  const tipoClienteOptions = useMemo(
    () =>
      catalogos.tipoClientes?.length
        ? catalogos.tipoClientes
            .map((item) => item?.Descripcion)
            .filter(Boolean)
        : [],
    [catalogos.tipoClientes],
  );
  useEffect(() => {
    const currentTipoCliente = String(form?.tipoCliente ?? "").trim();
    if (currentTipoCliente) return;
    const firstTipoCliente = String(tipoClienteOptions?.[0] ?? "").trim();
    if (!firstTipoCliente) return;

    setForm((prev) => ({ ...prev, tipoCliente: firstTipoCliente }));
    setFieldErrors((prev) => {
      if (!prev?.tipoCliente) return prev;
      const next = { ...prev };
      delete next.tipoCliente;
      return next;
    });
  }, [form.tipoCliente, tipoClienteOptions]);
  const profesionOptions = useMemo(
    () =>
      catalogos.profesiones?.length
        ? catalogos.profesiones.map((item) => item?.Descripcion).filter(Boolean)
        : [],
    [catalogos.profesiones],
  );
  const actividadEconomicaCnbsOptions = useMemo(
    () =>
      catalogos.actividadesEconomicasCnbs?.length
        ? catalogos.actividadesEconomicasCnbs
            .map((item) => item?.NombreActividadEconomica)
            .filter(Boolean)
        : [],
    [catalogos.actividadesEconomicasCnbs],
  );
  const profesionCnbsOptions = useMemo(
    () =>
      catalogos.profesionesCnbs?.length
        ? catalogos.profesionesCnbs
            .map((item) => item?.NombreProfesionCnbs)
            .filter(Boolean)
        : [],
    [catalogos.profesionesCnbs],
  );
  const nivelEducativoOptions = useMemo(() => {
    const base = [...(catalogos.nivelesEducativos || [])];
    base.sort((a, b) => {
      const aRaw = String(a?.NivelEducativoId ?? "").trim();
      const bRaw = String(b?.NivelEducativoId ?? "").trim();
      const aNum = Number(aRaw);
      const bNum = Number(bRaw);
      const aIsNum = Number.isFinite(aNum);
      const bIsNum = Number.isFinite(bNum);
      if (aIsNum && bIsNum) return aNum - bNum;
      if (aIsNum) return -1;
      if (bIsNum) return 1;
      return aRaw.localeCompare(bRaw, "es", {
        numeric: true,
        sensitivity: "base",
      });
    });
    return base.map((item) => item?.Descripcion).filter(Boolean);
  }, [catalogos.nivelesEducativos]);

  useEffect(() => {
    if (tab > afiliacionTabs.length - 1) {
      setTab(0);
      return;
    }
    if (!form.esPep && currentTabName === "Persona Políticamente Expuesta") {
      const beneficiariosIndex = afiliacionTabs.indexOf("Beneficiarios");
      setTab(beneficiariosIndex >= 0 ? beneficiariosIndex : 0);
    }
  }, [afiliacionTabs, currentTabName, form.esPep, tab]);

  const handleChange = (field) => (event) => {
    const raw =
      event?.target?.type === "checkbox"
        ? event.target.checked
        : event?.target?.value;
    let value =
      typeof raw === "string"
        ? raw.replace(/[\r\n]+/g, " ").replace(/  +/g, " ")
        : raw;
    if (typeof value === "string") {
      if (_NAME_FIELDS.includes(field)) {
        value = value.replace(_ONLY_LETTERS, "").toUpperCase();
      } else if (_NUM_FIELDS.includes(field)) {
        value = value.replace(_ONLY_DIGITS, "");
      } else if (_UPPER_FIELDS.includes(field)) {
        value = value.toUpperCase();
      } else if (field === "correo") {
        value = value.replace(/[ñÑ]/g, "");
      }
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (field === "correo" && typeof value === "string" && value.trim()) {
        if (!_EMAIL_REGEX.test(value.trim())) {
          return { ...prev, correo: "Formato de correo inválido" };
        }
      }
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const disabledNext = useMemo(() => false, []);
  const brandColor = "#19498c";

  const isBlank = (value) => `${value ?? ""}`.trim() === "";
  const asText = (value) =>
    value === undefined || value === null ? "" : `${value}`.trim();
  const normalizeText = (value) => asText(value).toUpperCase();
  const normalizeSearchText = (value) =>
    asText(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();
  const normalizeCorreoConArroba = (value) => {
    const cleaned = asText(value).replace(/\s+/g, "").replace(/[ñÑ]/g, "");
    if (!cleaned) return "";
    if (cleaned.includes("@")) return cleaned;

    const lower = cleaned.toLowerCase();
    const knownDomains = [
      "gmail.com",
      "hotmail.com",
      "outlook.com",
      "yahoo.com",
      "icloud.com",
      "injupemp.gob.hn",
      "injupemp.hn",
    ];
    for (const domain of knownDomains) {
      if (!lower.endsWith(domain)) continue;
      const localPart = cleaned
        .slice(0, cleaned.length - domain.length)
        .replace(/[@._-]+$/, "");
      if (localPart) return `${localPart}@${domain}`;
    }
    return `${cleaned}@`;
  };
  const handleCorreoBlur = () => {
    if (noCorreo) return;
    const normalized = normalizeCorreoConArroba(form.correo);
    if (normalized !== form.correo) {
      setForm((prev) => ({ ...prev, correo: normalized }));
    }
    setFieldErrors((prev) => {
      if (!normalized) {
        if (!prev.correo) return prev;
        const next = { ...prev };
        delete next.correo;
        return next;
      }
      if (!_EMAIL_REGEX.test(normalized.trim())) {
        return { ...prev, correo: "Formato de correo inválido" };
      }
      if (!prev.correo) return prev;
      const next = { ...prev };
      delete next.correo;
      return next;
    });
  };
  const filterTextOptions = (options, state) => {
    const query = normalizeSearchText(state?.inputValue);
    if (!query) return options;
    return (options || []).filter((option) =>
      normalizeSearchText(option).includes(query),
    );
  };
  const filterActividadEconomicaCnbsOptions = (options, state) => {
    const query = normalizeSearchText(state?.inputValue);
    if (!query) return options;
    return (options || []).filter((option) =>
      normalizeSearchText(option).includes(query),
    );
  };
  const appendCurrentOption = (baseOptions, currentValue) => {
    const current = asText(currentValue);
    const unique = Array.from(
      new Set((baseOptions || []).map((item) => asText(item)).filter(Boolean)),
    );
    if (!current) return unique;
    if (unique.some((item) => normalizeText(item) === normalizeText(current))) {
      return unique;
    }
    return [current, ...unique];
  };
  const parseIsoDate = (value) => {
    const raw = asText(value);
    if (!raw) return null;
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const parsed = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
    );
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  };
  const calculateYearsElapsed = (fromDate, toDate = new Date()) => {
    if (!(fromDate instanceof Date) || Number.isNaN(fromDate.getTime()))
      return "";
    if (!(toDate instanceof Date) || Number.isNaN(toDate.getTime())) return "";
    if (fromDate > toDate) return "0";

    let years = toDate.getFullYear() - fromDate.getFullYear();
    const monthDiff = toDate.getMonth() - fromDate.getMonth();
    const dayDiff = toDate.getDate() - fromDate.getDate();
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      years -= 1;
    }
    return String(Math.max(0, years));
  };
  const clampTiempoResidirValue = (value) => {
    const raw = asText(value);
    if (!raw) return "";
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return "";
    return String(Math.min(MAX_TIEMPO_RESIDIR, Math.max(0, parsed)));
  };
  const buildFechaDesdeResideFromYears = (yearsRaw) => {
    const parsedYears = Number.parseInt(clampTiempoResidirValue(yearsRaw), 10);
    if (!Number.isFinite(parsedYears) || parsedYears < 0) return "";
    const today = new Date();
    const target = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    target.setFullYear(target.getFullYear() - parsedYears);
    const yyyy = target.getFullYear();
    const mm = String(target.getMonth() + 1).padStart(2, "0");
    const dd = String(target.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const buildAntiguedadLaboral = (isoDate) => {
    const ingresoDate = parseIsoDate(isoDate);
    if (!ingresoDate) return "";
    const today = new Date();
    if (ingresoDate > today) return "0 AÑOS, 0 MESES, 0 DÍAS";

    let years = today.getFullYear() - ingresoDate.getFullYear();
    let months = today.getMonth() - ingresoDate.getMonth();
    let days = today.getDate() - ingresoDate.getDate();

    if (days < 0) {
      months -= 1;
      const prevMonthLastDay = new Date(
        today.getFullYear(),
        today.getMonth(),
        0,
      ).getDate();
      days += prevMonthLastDay;
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return `${Math.max(0, years)} AÑOS, ${Math.max(0, months)} MESES, ${Math.max(0, days)} DÍAS`;
  };

  const departamentoResidenciaOptions = useMemo(
    () =>
      appendCurrentOption(
        (catalogos.departamentos || []).map((item) => item?.Nombre),
        form.departamentoResidencia,
      ),
    [catalogos.departamentos, form.departamentoResidencia],
  );
  const selectedDepartamentoResidenciaId = useMemo(() => {
    const current = normalizeText(form.departamentoResidencia);
    if (!current) return "";
    const match = (catalogos.departamentos || []).find((item) => {
      const idText = normalizeText(item?.DepartamentoId);
      const nombre = normalizeText(item?.Nombre);
      return current === idText || current === nombre;
    });
    return asText(match?.DepartamentoId);
  }, [catalogos.departamentos, form.departamentoResidencia]);
  const municipiosResidenciaFiltered = useMemo(
    () =>
      (catalogos.municipios || []).filter((item) => {
        if (!selectedDepartamentoResidenciaId) return true;
        return (
          asText(item?.FKDepartamentoId) ===
          asText(selectedDepartamentoResidenciaId)
        );
      }),
    [catalogos.municipios, selectedDepartamentoResidenciaId],
  );
  const municipioResidenciaOptions = useMemo(
    () =>
      appendCurrentOption(
        municipiosResidenciaFiltered.map((item) => item?.Nombre),
        form.municipioResidencia,
      ),
    [municipiosResidenciaFiltered, form.municipioResidencia],
  );
  const selectedMunicipioResidenciaId = useMemo(() => {
    const current = normalizeText(form.municipioResidencia);
    if (!current) return "";
    const source = municipiosResidenciaFiltered.length
      ? municipiosResidenciaFiltered
      : catalogos.municipios || [];
    const match = source.find((item) => {
      const idText = normalizeText(item?.MunicipioId);
      const nombre = normalizeText(item?.Nombre);
      return current === idText || current === nombre;
    });
    return asText(match?.MunicipioId);
  }, [
    catalogos.municipios,
    municipiosResidenciaFiltered,
    form.municipioResidencia,
  ]);
  const coloniasResidenciaFiltered = useMemo(
    () =>
      (catalogos.colonias || []).filter((item) => {
        if (!selectedMunicipioResidenciaId) return true;
        return (
          asText(item?.FKMunicipioId) === asText(selectedMunicipioResidenciaId)
        );
      }),
    [catalogos.colonias, selectedMunicipioResidenciaId],
  );
  const coloniaResidenciaOptions = useMemo(
    () =>
      appendCurrentOption(
        coloniasResidenciaFiltered.map((item) => item?.Nombre),
        form.coloniaResidencia,
      ),
    [coloniasResidenciaFiltered, form.coloniaResidencia],
  );
  const departamentoTrabajoOptions = useMemo(
    () =>
      appendCurrentOption(
        (catalogos.departamentos || []).map((item) => item?.Nombre),
        form.departamentoTrabajo,
      ),
    [catalogos.departamentos, form.departamentoTrabajo],
  );
  const selectedDepartamentoTrabajoId = useMemo(() => {
    const current = normalizeText(form.departamentoTrabajo);
    if (!current) return "";
    const match = (catalogos.departamentos || []).find((item) => {
      const idText = normalizeText(item?.DepartamentoId);
      const nombre = normalizeText(item?.Nombre);
      return current === idText || current === nombre;
    });
    return asText(match?.DepartamentoId);
  }, [catalogos.departamentos, form.departamentoTrabajo]);
  const municipiosTrabajoFiltered = useMemo(
    () =>
      (catalogos.municipios || []).filter((item) => {
        if (!selectedDepartamentoTrabajoId) return true;
        return (
          asText(item?.FKDepartamentoId) ===
          asText(selectedDepartamentoTrabajoId)
        );
      }),
    [catalogos.municipios, selectedDepartamentoTrabajoId],
  );
  const municipioTrabajoOptions = useMemo(
    () =>
      appendCurrentOption(
        municipiosTrabajoFiltered.map((item) => item?.Nombre),
        form.municipioTrabajo,
      ),
    [municipiosTrabajoFiltered, form.municipioTrabajo],
  );
  const selectedMunicipioTrabajoId = useMemo(() => {
    const current = normalizeText(form.municipioTrabajo);
    if (!current) return "";
    const source = municipiosTrabajoFiltered.length
      ? municipiosTrabajoFiltered
      : catalogos.municipios || [];
    const match = source.find((item) => {
      const idText = normalizeText(item?.MunicipioId);
      const nombre = normalizeText(item?.Nombre);
      return current === idText || current === nombre;
    });
    return asText(match?.MunicipioId);
  }, [catalogos.municipios, municipiosTrabajoFiltered, form.municipioTrabajo]);
  const coloniasTrabajoFiltered = useMemo(
    () =>
      (catalogos.colonias || []).filter((item) => {
        if (!selectedMunicipioTrabajoId) return true;
        return (
          asText(item?.FKMunicipioId) === asText(selectedMunicipioTrabajoId)
        );
      }),
    [catalogos.colonias, selectedMunicipioTrabajoId],
  );
  const coloniaTrabajoOptions = useMemo(
    () =>
      appendCurrentOption(
        coloniasTrabajoFiltered.map((item) => item?.Nombre),
        form.coloniaTrabajo,
      ),
    [coloniasTrabajoFiltered, form.coloniaTrabajo],
  );
  const ingresoAproxRangoOptions = useMemo(
    () =>
      appendCurrentOption(
        (catalogos.ingresosAproximados || []).map((item) => item?.Ingresos),
        form.ingresoAproxRango,
      ),
    [catalogos.ingresosAproximados, form.ingresoAproxRango],
  );
  const giroEntidadOptions = useMemo(
    () =>
      appendCurrentOption(
        (catalogos.girosEntidad || []).map((item) => item?.Descripcion),
        form.giroEntidad,
      ),
    [catalogos.girosEntidad, form.giroEntidad],
  );
  const cargoDesempenadoOptions = useMemo(
    () =>
      appendCurrentOption(
        [...(catalogos.cargos || [])]
          .sort((a, b) => {
            const aRaw = asText(a?.CargoId);
            const bRaw = asText(b?.CargoId);
            const aNum = Number(aRaw);
            const bNum = Number(bRaw);
            const aIsNum = Number.isFinite(aNum);
            const bIsNum = Number.isFinite(bNum);
            if (aIsNum && bIsNum) return aNum - bNum;
            if (aIsNum) return -1;
            if (bIsNum) return 1;
            return aRaw.localeCompare(bRaw, "es", {
              numeric: true,
              sensitivity: "base",
            });
          })
          .map((item) => item?.Descripcion),
        form.cargoDesempenado,
      ),
    [catalogos.cargos, form.cargoDesempenado],
  );
  const tipoViviendaOptions = useMemo(
    () =>
      appendCurrentOption(
        (catalogos.viviendaTipos || []).map((item) => item?.Descripcion),
        form.tipoVivienda,
      ),
    [catalogos.viviendaTipos, form.tipoVivienda],
  );
  const cargoCatalogOptions = useMemo(
    () =>
      appendCurrentOption(
        [...(catalogos.cargos || [])]
          .sort((a, b) => {
            const aRaw = asText(a?.CargoId);
            const bRaw = asText(b?.CargoId);
            const aNum = Number(aRaw);
            const bNum = Number(bRaw);
            const aIsNum = Number.isFinite(aNum);
            const bIsNum = Number.isFinite(bNum);
            if (aIsNum && bIsNum) return aNum - bNum;
            if (aIsNum) return -1;
            if (bIsNum) return 1;
            return aRaw.localeCompare(bRaw, "es", {
              numeric: true,
              sensitivity: "base",
            });
          })
          .map((item) => item?.Descripcion),
        cargoForm.cargoDesempenado,
      ),
    [catalogos.cargos, cargoForm.cargoDesempenado],
  );
  const giroCatalogOptions = useMemo(
    () =>
      appendCurrentOption(
        (catalogos.girosEntidad || []).map((item) => item?.Descripcion),
        cargoForm.actividadGiro,
      ),
    [catalogos.girosEntidad, cargoForm.actividadGiro],
  );
  const giroNegocioCatalogOptions = useMemo(
    () =>
      appendCurrentOption(
        (catalogos.girosEntidad || []).map((item) => item?.Descripcion),
        ppeEmpresaForm.giroNegocio,
      ),
    [catalogos.girosEntidad, ppeEmpresaForm.giroNegocio],
  );
  const profesionSearchOptions = useMemo(
    () => appendCurrentOption(profesionOptions, form.profesion),
    [profesionOptions, form.profesion],
  );
  const actividadEconomicaCnbsSearchOptions = useMemo(
    () =>
      appendCurrentOption(
        actividadEconomicaCnbsOptions,
        form.actividadEconomicaCnbs,
      ),
    [actividadEconomicaCnbsOptions, form.actividadEconomicaCnbs],
  );
  const profesionCnbsSearchOptions = useMemo(
    () => appendCurrentOption(profesionCnbsOptions, form.profesionCnbs),
    [profesionCnbsOptions, form.profesionCnbs],
  );
  const documentoTipoConyugeOptions = useMemo(
    () => appendCurrentOption(documentoTipoOptions, form.tipoDocumentoConyuge),
    [documentoTipoOptions, form.tipoDocumentoConyuge],
  );
  const sexoConyugeOptions = useMemo(() => {
    const opciones = (catalogos.sexos || [])
      .map((item) => asText(item?.Descripcion))
      .filter(Boolean);
    return appendCurrentOption(
      opciones.length ? opciones : ["MASCULINO", "FEMENINO"],
      form.generoConyuge,
    );
  }, [catalogos.sexos, form.generoConyuge]);

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };
  const handleDepartamentoResidenciaSelect = (eventOrValue) => {
    const value =
      typeof eventOrValue === "string"
        ? eventOrValue
        : (eventOrValue?.target?.value ?? "");
    setForm((prev) => ({
      ...prev,
      departamentoResidencia: value,
      municipioResidencia: "",
      coloniaResidencia: "",
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.departamentoResidencia;
      delete next.municipioResidencia;
      delete next.coloniaResidencia;
      return next;
    });
  };
  const handleMunicipioResidenciaSelect = (eventOrValue) => {
    const value =
      typeof eventOrValue === "string"
        ? eventOrValue
        : (eventOrValue?.target?.value ?? "");
    setForm((prev) => ({
      ...prev,
      municipioResidencia: value,
      coloniaResidencia: "",
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.municipioResidencia;
      delete next.coloniaResidencia;
      return next;
    });
  };
  const handleColoniaResidenciaSelect = (value) => {
    setForm((prev) => ({ ...prev, coloniaResidencia: value || "" }));
    clearFieldError("coloniaResidencia");
  };
  const handleDepartamentoTrabajoSelect = (value) => {
    setForm((prev) => ({
      ...prev,
      departamentoTrabajo: value || "",
      municipioTrabajo: "",
      coloniaTrabajo: "",
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.departamentoTrabajo;
      delete next.municipioTrabajo;
      delete next.coloniaTrabajo;
      return next;
    });
  };
  const handleMunicipioTrabajoSelect = (value) => {
    setForm((prev) => ({
      ...prev,
      municipioTrabajo: value || "",
      coloniaTrabajo: "",
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.municipioTrabajo;
      delete next.coloniaTrabajo;
      return next;
    });
  };
  const handleColoniaTrabajoSelect = (value) => {
    setForm((prev) => ({ ...prev, coloniaTrabajo: value || "" }));
    clearFieldError("coloniaTrabajo");
  };
  const handleTiempoResidirChange = (event) => {
    const value = clampTiempoResidirValue(event?.target?.value ?? "");
    setForm((prev) => ({
      ...prev,
      tiempoResidir: value,
      fechaDesdeReside:
        String(value).trim() === ""
          ? ""
          : buildFechaDesdeResideFromYears(value) || prev.fechaDesdeReside,
    }));
    clearFieldError("tiempoResidir");
    clearFieldError("fechaDesdeReside");
  };
  const handleFechaDesdeResideChange = (event) => {
    const value = event?.target?.value ?? "";
    const parsedDate = parseIsoDate(value);
    setForm((prev) => ({
      ...prev,
      fechaDesdeReside: value,
      tiempoResidir:
        value === ""
          ? ""
          : parsedDate
            ? clampTiempoResidirValue(calculateYearsElapsed(parsedDate))
            : prev.tiempoResidir,
    }));
    clearFieldError("fechaDesdeReside");
    clearFieldError("tiempoResidir");
  };
  const handleFechaIngresoInstitucionChange = (event) => {
    const value = event?.target?.value ?? "";
    setForm((prev) => ({
      ...prev,
      fechaIngresoInstitucion: value,
      antiguedadLaboral: buildAntiguedadLaboral(value),
    }));
    clearFieldError("fechaIngresoInstitucion");
    clearFieldError("antiguedadLaboral");
  };
  const handleIngresoAproxRangoSelect = (value) => {
    setForm((prev) => ({ ...prev, ingresoAproxRango: value || "" }));
    clearFieldError("ingresoAproxRango");
  };
  const handleGiroEntidadSelect = (value) => {
    setForm((prev) => ({ ...prev, giroEntidad: value || "" }));
    clearFieldError("giroEntidad");
  };
  const handleCargoDesempenadoSelect = (value) => {
    setForm((prev) => ({ ...prev, cargoDesempenado: value || "" }));
    clearFieldError("cargoDesempenado");
  };
  const handleTipoViviendaSelect = (value) => {
    setForm((prev) => ({ ...prev, tipoVivienda: value || "" }));
    clearFieldError("tipoVivienda");
  };
  const handleCargoModalCatalogSelect = (field, value) => {
    setCargoForm((prev) => ({ ...prev, [field]: value || "" }));
    setCargoModalError((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };
  const handlePpeGiroNegocioSelect = (value) => {
    setPpeEmpresaForm((prev) => ({ ...prev, giroNegocio: value || "" }));
    setPpeEmpresaError((prev) => {
      if (!prev.giroNegocio) return prev;
      const next = { ...prev };
      delete next.giroNegocio;
      return next;
    });
  };
  const resolveParentescoId = (value) => {
    const current = asText(value);
    if (!current) return "";
    const found = (catalogos.parentescos || []).find((item) => {
      const id = asText(item?.ParentescoId);
      const label = asText(item?.Descripcion);
      return current === id || normalizeText(current) === normalizeText(label);
    });
    return asText(found?.ParentescoId || current);
  };
  const resolveNacionalidadId = (value) => {
    const current = asText(value);
    if (!current) return "";
    const found = (catalogos.nacionalidades || []).find((item) => {
      const id = asText(item?.NacionalidadId);
      const descripcion = asText(item?.Descripcion);
      const gentilicio = asText(item?.GentilicioDisplay || item?.Gentilicio);
      return (
        current === id ||
        normalizeText(current) === normalizeText(descripcion) ||
        normalizeText(current) === normalizeText(gentilicio)
      );
    });
    return asText(found?.NacionalidadId || current);
  };
  const resolveGiroEntidadId = (value) => {
    const current = asText(value);
    if (!current) return "";
    const found = (catalogos.girosEntidad || []).find((item) => {
      const id = asText(item?.GiroEntidadId);
      const descripcion = asText(item?.Descripcion);
      return (
        current === id || normalizeText(current) === normalizeText(descripcion)
      );
    });
    return asText(found?.GiroEntidadId || current);
  };
  const handleProfesionSelect = (value) => {
    setForm((prev) => ({ ...prev, profesion: value || "" }));
    clearFieldError("profesion");
  };
  const handleActividadEconomicaCnbsSelect = (value) => {
    setForm((prev) => ({ ...prev, actividadEconomicaCnbs: value || "" }));
    clearFieldError("actividadEconomicaCnbs");
  };
  const handleProfesionCnbsSelect = (value) => {
    setForm((prev) => ({ ...prev, profesionCnbs: value || "" }));
    clearFieldError("profesionCnbs");
  };

  const toUiDate = (value) => {
    const raw = asText(value);
    if (!raw) return "";
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
    return raw;
  };

  const normalizeDateFromAfiliadoApi = (value) => {
    const raw = asText(value);
    if (!raw) return "";
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

    const dmyDash = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmyDash) return `${dmyDash[3]}-${dmyDash[2]}-${dmyDash[1]}`;

    const dmySlash = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dmySlash) return `${dmySlash[3]}-${dmySlash[2]}-${dmySlash[1]}`;

    return "";
  };

  const normalizeSexoFromAfiliadoApi = (value) => {
    const normalized = asText(value).toUpperCase();
    if (!normalized) return "";
    if (normalized === "M" || normalized.includes("MASC")) return "M";
    if (normalized === "F" || normalized.includes("FEM")) return "F";
    return "";
  };

  const normalizeEstadoCivilFromAfiliadoApi = (value) => {
    const normalized = asText(value).toUpperCase();
    if (!normalized) return "";
    const normalizeForMatch = (text) =>
      asText(text)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[_-]/g, " ")
        .toUpperCase();
    const normalizedInput = normalizeForMatch(normalized);
    const compactInput = normalizedInput.replace(/\s+/g, "");

    const byExactOption = estadoCivilOptions.find((option) => {
      const optionValue = normalizeForMatch(option.value);
      const optionLabel = normalizeForMatch(option.label);
      const compactValue = optionValue.replace(/\s+/g, "");
      const compactLabel = optionLabel.replace(/\s+/g, "");
      return (
        normalizedInput === optionValue ||
        normalizedInput === optionLabel ||
        compactInput === compactValue ||
        compactInput === compactLabel
      );
    });
    if (byExactOption) return asText(byExactOption.value);

    if (compactInput === "S" || normalizedInput.includes("SOLTER")) {
      const option = estadoCivilOptions.find((item) => {
        const label = normalizeForMatch(item.label);
        const value = normalizeForMatch(item.value);
        return label.includes("SOLTER") || value === "S";
      });
      return asText(option?.value || "S");
    }
    if (compactInput === "C" || normalizedInput.includes("CASAD")) {
      const option = estadoCivilOptions.find((item) => {
        const label = normalizeForMatch(item.label);
        const value = normalizeForMatch(item.value);
        return label.includes("CASAD") || value === "C";
      });
      return asText(option?.value || "C");
    }
    if (
      compactInput === "U" ||
      compactInput === "UL" ||
      normalizedInput.includes("UNION")
    ) {
      const option = estadoCivilOptions.find((item) =>
        normalizeForMatch(item.label).includes("UNION"),
      );
      return asText(option?.value || "U");
    }
    return asText(value);
  };

  const extractMovilFromDireccion = (direccion) => {
    const text = asText(direccion);
    if (!text) return "";
    const celMatch = text.match(
      /(?:CEL|CEL\.?|CELULAR)\s*[:\-]?\s*([0-9\-]+)/i,
    );
    if (!celMatch) return "";
    return asText(celMatch[1]);
  };

  const mapPersonaExistenteForModal = (
    personaPayload,
    searchPayload = null,
  ) => {
    const personaData = personaPayload || {};
    const identificacion = asText(
      personaData.DocumentoNumero ||
        personaData.PersonaId ||
        form.identificacion,
    );
    const primerNombre = asText(personaData.Nombre1);
    const segundoNombre = asText(personaData.Nombre2);
    const primerApellido = asText(personaData.Apellido1);
    const segundoApellido = asText(personaData.Apellido2);
    const nombres = [primerNombre, segundoNombre]
      .filter(Boolean)
      .join(" ")
      .trim();
    const apellidos = [primerApellido, segundoApellido]
      .filter(Boolean)
      .join(" ")
      .trim();
    const sexoRaw = asText(personaData.SexoDescripcion || personaData.FKSexoId);
    const sexo =
      normalizeSexoFromAfiliadoApi(sexoRaw) === "M"
        ? "MASCULINO"
        : normalizeSexoFromAfiliadoApi(sexoRaw) === "F"
          ? "FEMENINO"
          : sexoRaw.toUpperCase();
    const fotoUrl = getUltimaFotoPersona(searchPayload || personaData);
    return {
      identificacion,
      nombres,
      apellidos,
      fechaNacimiento: toUiDate(personaData.FechaNacimiento),
      sexo,
      fotoUrl,
    };
  };

  const prefillFromAfiliadoExterno = (afiliadoPayload) => {
    const afiliado = afiliadoPayload || {};
    const primerNombre = asText(
      afiliado.primer_nombre || afiliado.primerNombre || afiliado.nombre1,
    );
    const segundoNombre = asText(
      afiliado.segundo_nombre || afiliado.segundoNombre || afiliado.nombre2,
    );
    const primerApellido = asText(
      afiliado.primer_apellido || afiliado.primerApellido || afiliado.apellido1,
    );
    const segundoApellido = asText(
      afiliado.segundo_apellido ||
        afiliado.segundoApellido ||
        afiliado.apellido2,
    );
    const direccion = asText(afiliado.direccion);
    const telefonoFijo = asText(
      afiliado.telefono_fijo || afiliado.telefonoFijo || afiliado.telefono,
    );
    const fechaNacimiento = normalizeDateFromAfiliadoApi(
      afiliado.fecha_nacimiento || afiliado.fechaNacimiento,
    );
    const estadoCivil = normalizeEstadoCivilFromAfiliadoApi(
      afiliado.estado_civil || afiliado.estadoCivil,
    );
    const genero = normalizeSexoFromAfiliadoApi(
      afiliado.sexo || afiliado.genero,
    );
    const movil = asText(
      afiliado.telefono_movil ||
        afiliado.telefonoMovil ||
        extractMovilFromDireccion(direccion),
    );

    setForm((prev) => ({
      ...prev,
      primerNombre: primerNombre || prev.primerNombre,
      segundoNombre: segundoNombre || prev.segundoNombre,
      primerApellido: primerApellido || prev.primerApellido,
      segundoApellido: segundoApellido || prev.segundoApellido,
      direccionCompleta: direccion || prev.direccionCompleta,
      telefono: telefonoFijo || prev.telefono,
      movil: movil || prev.movil,
      fechaNacimiento: fechaNacimiento || prev.fechaNacimiento,
      estadoCivil: estadoCivil || prev.estadoCivil,
      genero: genero || prev.genero,
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      [
        "primerNombre",
        "segundoNombre",
        "primerApellido",
        "segundoApellido",
        "direccionCompleta",
        "telefono",
        "movil",
        "fechaNacimiento",
        "estadoCivil",
        "genero",
      ].forEach((field) => delete next[field]);
      return next;
    });
  };

  const validateDatosPersonales = () => {
    const errors = {};
    const requiredFields = [
      "primerNombre",
      "primerApellido",
      "genero",
      "paisNacimiento",
      "nacionalidad",
      "fechaNacimiento",
      "lugarNacimiento",
      "estadoCivil",
      "departamentoResidencia",
      "municipioResidencia",
      "coloniaResidencia",
      "direccionCompleta",
      "fechaDesdeReside",
      "movil",
      "tipoCliente",
      "profesion",
      "actividadEconomicaCnbs",
      "profesionCnbs",
      "nivelEducativo",
    ];

    requiredFields.forEach((field) => {
      if (isBlank(form[field])) errors[field] = "Campo obligatorio";
    });

    if (!noCorreo && isBlank(form.correo)) {
      errors.correo = "Campo obligatorio";
    } else if (
      !noCorreo &&
      !isBlank(form.correo) &&
      !_EMAIL_REGEX.test(form.correo.trim())
    ) {
      errors.correo = "Formato de correo inválido";
    }

    return errors;
  };

  const requiredInformacionLaboralFields = [
    "patrono",
    "departamentoTrabajo",
    "municipioTrabajo",
    "coloniaTrabajo",
    "fechaIngresoInstitucion",
    "antiguedadLaboral",
    "ingresoAproxRango",
    "ingresoAproxMonto",
    "cargoDesempenado",
    "telefonoTrabajo",
  ];
  const requiredPatrimonioFields = [
    "tipoVivienda",
    "poseeVehiculo",
    "numeroVehiculos",
    "poseeInmuebles",
    "descOtrosInmuebles",
    "otrosIngresos",
    "descOtrosIngresos",
    "montoOtrosIngresos",
    "dependientesEconomicos",
  ];

  const validateInformacionLaboral = () => {
    const errors = {};
    requiredInformacionLaboralFields.forEach((field) => {
      if (isBlank(form[field])) errors[field] = "Campo obligatorio";
    });
    return errors;
  };

  const validatePatrimonio = () => {
    const errors = {};
    if (isBlank(form.tipoVivienda)) {
      errors.tipoVivienda = "Campo obligatorio";
    }
    if (isBlank(form.poseeVehiculo)) {
      errors.poseeVehiculo = "Campo obligatorio";
    }
    if (form.poseeVehiculo === "Si" && isBlank(form.numeroVehiculos)) {
      errors.numeroVehiculos = "Campo obligatorio";
    }
    if (isBlank(form.poseeInmuebles)) {
      errors.poseeInmuebles = "Campo obligatorio";
    }
    if (form.poseeInmuebles === "Si" && isBlank(form.descOtrosInmuebles)) {
      errors.descOtrosInmuebles = "Campo obligatorio";
    }
    if (isBlank(form.otrosIngresos)) {
      errors.otrosIngresos = "Campo obligatorio";
    }
    if (form.otrosIngresos === "Si") {
      if (isBlank(form.descOtrosIngresos)) {
        errors.descOtrosIngresos = "Campo obligatorio";
      }
      if (isBlank(form.montoOtrosIngresos)) {
        errors.montoOtrosIngresos = "Campo obligatorio";
      }
    }
    if (isBlank(form.dependientesEconomicos)) {
      errors.dependientesEconomicos = "Campo obligatorio";
    }
    return errors;
  };

  const handleBuscar = async () => {
    const identity = asText(form.identificacion);
    if (!identity) {
      setSearchError("Ingrese un número de identificación para continuar.");
      return;
    }

    setSearchError("");
    setOpenPersonaExistenteModal(false);
    setPersonaExistente(null);
    setSearchingPersona(true);

    try {
      let personaRegistrada = null;
      let existenteResponse = null;
      try {
        existenteResponse = await apiRequest(
          `/Enrolamiento/personas/buscar/?dni=${encodeURIComponent(identity)}`,
          { authToken },
        );
        personaRegistrada = existenteResponse?.persona || null;
      } catch (error) {
        const errorMessage = asText(error?.message).toLowerCase();
        const isNotFound =
          errorMessage.includes("no se encontró la persona solicitada") ||
          errorMessage.includes("http 404") ||
          errorMessage.includes("404");
        if (!isNotFound) {
          setSearchError(
            error?.message || "No se pudo validar el DNI en la base de datos.",
          );
          return;
        }
      }

      if (personaRegistrada) {
        setPersonaExistente(
          mapPersonaExistenteForModal(personaRegistrada, existenteResponse),
        );
        setOpenPersonaExistenteModal(true);
        return;
      }

      if (afiliadoInjupemp) {
        let afiliadoResponse;
        try {
          afiliadoResponse = await apiRequest(
            `/Enrolamiento/afiliados/consulta/?dni=${encodeURIComponent(identity)}`,
            { authToken },
          );
        } catch (error) {
          setSearchError(
            error?.message || "No se pudo consultar la validación de afiliado.",
          );
          return;
        }

        if (!afiliadoResponse?.encontrado || !afiliadoResponse?.afiliado) {
          setSearchError("La persona no es afiliado al injupemp.");
          return;
        }

        // Pre-llena todos los campos desde el API de afiliados (incluye nombres
        // como fallback en caso de que el RNP no responda).
        prefillFromAfiliadoExterno(afiliadoResponse.afiliado);
      }

      // ── Consulta RNP: los nombres del RNP tienen prioridad ────────────────
      let rnpNombresOk = false;
      try {
        const rnpResponse = await consultarRnpIdentidad(authToken, identity);
        if (rnpResponse?.encontrado && rnpResponse?.nombres) {
          const n = rnpResponse.nombres;
          setForm((prev) => ({
            ...prev,
            primerNombre: asText(n.primer_nombre) || prev.primerNombre,
            segundoNombre: asText(n.segundo_nombre) || prev.segundoNombre,
            primerApellido: asText(n.primer_apellido) || prev.primerApellido,
            segundoApellido: asText(n.segundo_apellido) || prev.segundoApellido,
          }));
          setFieldErrors((prev) => {
            const next = { ...prev };
            [
              "primerNombre",
              "segundoNombre",
              "primerApellido",
              "segundoApellido",
            ].forEach((f) => delete next[f]);
            return next;
          });
          rnpNombresOk = true;
        }
      } catch {
        // RNP no disponible, se intentará con el AS400
      }

      // ── Fallback AS400: si RNP no respondió y no se pre-llenó de afiliado ─
      if (!rnpNombresOk && !afiliadoInjupemp) {
        try {
          const as400Response = await apiRequest(
            `/Enrolamiento/afiliados/consulta/?dni=${encodeURIComponent(identity)}`,
            { authToken },
          );
          if (as400Response?.encontrado && as400Response?.afiliado) {
            prefillFromAfiliadoExterno(as400Response.afiliado);
          } else {
            setSearchError(
              "No hubo conexión con el AS400. Por favor ingrese los datos manualmente.",
            );
          }
        } catch {
          setSearchError(
            "No hubo conexión con el AS400. Por favor ingrese los datos manualmente.",
          );
        }
      }
      // ── Fin consulta RNP / AS400 ──────────────────────────────────────────

      setForm((prev) => ({ ...prev, identificacion: identity }));
      setTab(0);
      setStep(1);
    } finally {
      setSearchingPersona(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraStream(null);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOpen(false);
  };

  const openCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError(
        "Este navegador no soporta cámara o la página no está en HTTPS/localhost.",
      );
      return;
    }
    try {
      setCameraError("");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      setCameraStream(stream);
      setCameraOpen(true);
    } catch (error) {
      setCameraError(
        "No se pudo abrir la cámara. Verifique permisos del navegador.",
      );
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 720;
    canvas.height = video.videoHeight || 960;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL("image/jpeg", 0.9);
    setPhotoDataUrl(imageData);
    setPhotoError("");
    stopCamera();
  };

  const handleTomarFoto = async () => {
    if (photoBlocked) return;
    if (!cameraOpen) {
      await openCamera();
      return;
    }
    capturePhoto();
  };

  const handleOpenSummaryPhoto = async () => {
    setPrevSummaryPhoto(photoDataUrl);
    setSummaryPhotoOpen(true);
    await openCamera();
  };

  const handleCloseSummaryPhoto = (save = false) => {
    stopCamera();
    if (!save) setPhotoDataUrl(prevSummaryPhoto);
    setSummaryPhotoOpen(false);
    setCameraError("");
  };

  useEffect(() => {
    if (step !== 2) {
      stopCamera();
    }
  }, [step]);

  useEffect(() => {
    if (!cameraOpen || !cameraStream || !videoRef.current) return;
    const video = videoRef.current;
    video.srcObject = cameraStream;
    const playVideo = async () => {
      try {
        await video.play();
      } catch (error) {
        setCameraError(
          "La cámara abrió, pero el video no pudo iniciar automáticamente.",
        );
      }
    };
    if (video.readyState >= 2) {
      playVideo();
    } else {
      video.onloadedmetadata = () => {
        playVideo();
      };
    }
  }, [cameraOpen, cameraStream]);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const handleNextStep = () => {
    if (step === 1) {
      const errors = validateDatosPersonales();
      setFieldErrors(errors);
      if (enforceStepValidation && Object.keys(errors).length > 0) {
        return;
      }
    }
    if (step === 2) {
      if (photoBlocked) {
        if (isBlank(justificacionFoto)) {
          setPhotoError(
            "Debe escribir la justificación cuando no se pueda capturar la foto.",
          );
          if (enforceStepValidation) return;
        }
      } else if (!photoDataUrl) {
        setPhotoError(
          "Debe tomar la foto o marcar que no se puede capturar la foto.",
        );
        if (enforceStepValidation) return;
      }
      setPhotoError("");
    }
    if (step === 3 && enforceStepValidation) {
      const laboralErrors = validateInformacionLaboral();
      const patrimonioErrors = validatePatrimonio();
      const referenciasError = getReferenciasRequirementError();
      const beneficiariosError = getBeneficiariosRequirementError();
      const pepFamiliaresError =
        form.esPep && ppeFamiliares.length === 0
          ? "Debe registrar al menos un familiar cuando la persona es Políticamente Expuesta."
          : "";

      setFieldErrors((prev) => {
        const next = { ...prev };
        requiredInformacionLaboralFields.forEach((field) => {
          delete next[field];
        });
        requiredPatrimonioFields.forEach((field) => {
          delete next[field];
        });
        return { ...next, ...laboralErrors, ...patrimonioErrors };
      });
      setReferenciaRequirementError(referenciasError);
      setBeneficiariosRequirementError(beneficiariosError);
      setPpeFamiliarRequirementError(pepFamiliaresError);

      if (Object.keys(laboralErrors).length > 0) {
        setTab(0);
        return;
      }
      if (Object.keys(patrimonioErrors).length > 0) {
        setTab(1);
        return;
      }
      if (referenciasError) {
        const referenciasIndex = afiliacionTabs.indexOf("Referencias");
        setTab(referenciasIndex >= 0 ? referenciasIndex : 0);
        return;
      }
      if (pepFamiliaresError) {
        const pepIndex = afiliacionTabs.indexOf(
          "Persona Políticamente Expuesta",
        );
        setTab(pepIndex >= 0 ? pepIndex : 0);
        return;
      }
      if (beneficiariosError) {
        const beneficiariosIndex = afiliacionTabs.indexOf("Beneficiarios");
        setTab(beneficiariosIndex >= 0 ? beneficiariosIndex : 0);
        return;
      }
    }
    const nextStep = Math.min(step + 1, stepTitles.length - 1);
    setStep(nextStep);
  };

  const handleAfiliacionTabChange = (newTab) => {
    const fromTabName = afiliacionTabs[tab];
    const toTabName = afiliacionTabs[newTab];
    if (fromTabName === "Información Laboral") {
      const errors = validateInformacionLaboral();
      setFieldErrors((prev) => {
        const next = { ...prev };
        requiredInformacionLaboralFields.forEach((field) => {
          delete next[field];
        });
        return { ...next, ...errors };
      });
      if (
        enforceStepValidation &&
        toTabName !== "Información Laboral" &&
        Object.keys(errors).length > 0
      ) {
        return;
      }
    }
    if (fromTabName === "Patrimonio" && toTabName !== "Patrimonio") {
      const errors = validatePatrimonio();
      setFieldErrors((prev) => {
        const next = { ...prev };
        requiredPatrimonioFields.forEach((field) => {
          delete next[field];
        });
        return { ...next, ...errors };
      });
      if (enforceStepValidation && Object.keys(errors).length > 0) {
        return;
      }
    }
    if (fromTabName === "Referencias" && toTabName !== "Referencias") {
      const error = getReferenciasRequirementError();
      setReferenciaRequirementError(error);
      if (enforceStepValidation && error) {
        return;
      }
    }
    if (fromTabName === "Beneficiarios" && toTabName !== "Beneficiarios") {
      const error = getBeneficiariosRequirementError();
      setBeneficiariosRequirementError(error);
      if (enforceStepValidation && error) {
        return;
      }
    }
    setTab(newTab);
  };

  const handleCargoFormChange = (field) => (event) => {
    let value = event?.target?.value ?? "";
    if (field === "empleadorPatrono") value = value.toUpperCase();
    setCargoForm((prev) => ({ ...prev, [field]: value }));
    setCargoModalError((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const resetCargoModal = () => {
    const primerGiro = catalogos.girosEntidad?.[0]?.Descripcion ?? "";
    setCargoForm({
      empleadorPatrono: "",
      cargoDesempenado: "",
      actividadGiro: primerGiro,
    });
    setCargoModalError({});
  };

  const handleOpenCargoModal = (item = null) => {
    if (item) {
      setCargoForm({
        empleadorPatrono: item.empleadorPatrono,
        cargoDesempenado: item.cargoDesempenado,
        actividadGiro: item.actividadGiro,
      });
      setEditingCargoId(item.id);
    } else {
      resetCargoModal();
      setEditingCargoId(null);
    }
    setOpenCargoModal(true);
  };

  const handleCloseCargoModal = () => {
    setOpenCargoModal(false);
    setCargoModalError({});
    setEditingCargoId(null);
  };

  const handleAgregarCargo = () => {
    const errors = {};
    if (isBlank(cargoForm.empleadorPatrono)) {
      errors.empleadorPatrono = "Campo obligatorio";
    }
    if (isBlank(cargoForm.cargoDesempenado)) {
      errors.cargoDesempenado = "Campo obligatorio";
    }
    if (isBlank(cargoForm.actividadGiro)) {
      errors.actividadGiro = "Campo obligatorio";
    }
    if (Object.keys(errors).length > 0) {
      setCargoModalError(errors);
      return;
    }

    const updated = {
      empleadorPatrono: cargoForm.empleadorPatrono.trim(),
      cargoDesempenado: cargoForm.cargoDesempenado.trim(),
      actividadGiro: cargoForm.actividadGiro.trim(),
    };
    if (editingCargoId !== null) {
      setOtrosCargos((prev) =>
        prev.map((c) => (c.id === editingCargoId ? { ...c, ...updated } : c)),
      );
    } else {
      setOtrosCargos((prev) => [...prev, { id: Date.now(), ...updated }]);
    }
    setOpenCargoModal(false);
    resetCargoModal();
    setEditingCargoId(null);
  };

  const handleReferenciaFormChange = (field) => (event) => {
    let value = event?.target?.value ?? "";
    if (field === "nombreCompleto") {
      value = value.replace(_ONLY_LETTERS_NUMS, "").toUpperCase();
    } else if (field === "telefono") {
      value = value.replace(_ONLY_DIGITS, "");
    } else if (field === "direccion") {
      value = value.toUpperCase();
    }
    setReferenciaForm((prev) => ({ ...prev, [field]: value }));
    setReferenciaModalError((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const resetReferenciaModal = () => {
    setReferenciaForm({
      tipoReferencia: "COMERCIALES / BANCARIAS",
      nombreCompleto: "",
      telefono: "",
      direccion: "",
    });
    setReferenciaModalError({});
  };

  const handleOpenReferenciaModal = (ref = null) => {
    if (ref) {
      setReferenciaForm({
        tipoReferencia: ref.tipoReferencia,
        nombreCompleto: ref.nombreCompleto,
        telefono: ref.telefono,
        direccion: ref.direccion === "-" ? "" : ref.direccion,
      });
      setEditingReferenciaId(ref.id);
      setReferenciaModalError({});
    } else {
      resetReferenciaModal();
      setEditingReferenciaId(null);
    }
    setOpenReferenciaModal(true);
  };

  const handleCloseReferenciaModal = () => {
    setOpenReferenciaModal(false);
    setReferenciaModalError({});
    setEditingReferenciaId(null);
  };

  const normalizeReferenciaTipo = (value) => {
    const text = asText(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();
    if (!text) return "";
    if (text.includes("COMERCIAL") || text.includes("BANC"))
      return "COMERCIALES / BANCARIAS";
    if (text.includes("LABOR")) return "LABORALES";
    if (text.includes("PERSON")) return "PERSONALES";
    return text;
  };

  const getReferenceCountByType = (tipo) =>
    referencias.filter(
      (item) =>
        normalizeReferenciaTipo(item.tipoReferencia) ===
        normalizeReferenciaTipo(tipo),
    ).length;

  const getReferenciasRequirementError = () => {
    const faltantes = [];
    if (getReferenceCountByType("PERSONALES") < 2) {
      faltantes.push("2 Personales");
    }
    if (getReferenceCountByType("COMERCIALES / BANCARIAS") < 1) {
      faltantes.push("1 Comercial/Bancaria");
    }
    return faltantes.length
      ? `Faltan referencias obligatorias: ${faltantes.join(", ")}.`
      : "";
  };

  const getReferenciasRequirementSummary = () =>
    "Requeridas: 2 Personales y 1 Comercial/Bancaria.";

  const handleAgregarReferencia = () => {
    const errors = {};
    if (isBlank(referenciaForm.tipoReferencia)) {
      errors.tipoReferencia = "Campo obligatorio";
    }
    if (isBlank(referenciaForm.nombreCompleto)) {
      errors.nombreCompleto = "Campo obligatorio";
    }
    if (isBlank(referenciaForm.telefono)) {
      errors.telefono = "Campo obligatorio";
    }
    if (
      normalizeReferenciaTipo(referenciaForm.tipoReferencia) !==
        "COMERCIALES / BANCARIAS" &&
      isBlank(referenciaForm.direccion)
    ) {
      errors.direccion = "Campo obligatorio";
    }
    if (Object.keys(errors).length > 0) {
      setReferenciaModalError(errors);
      return;
    }

    const updatedRef = {
      tipoReferencia: referenciaForm.tipoReferencia,
      nombreCompleto: referenciaForm.nombreCompleto.trim(),
      direccion:
        normalizeReferenciaTipo(referenciaForm.tipoReferencia) ===
        "COMERCIALES / BANCARIAS"
          ? "-"
          : referenciaForm.direccion.trim(),
      telefono: referenciaForm.telefono.trim(),
    };

    if (editingReferenciaId !== null) {
      setReferencias((prev) =>
        prev.map((ref) =>
          ref.id === editingReferenciaId ? { ...ref, ...updatedRef } : ref,
        ),
      );
      setEditingReferenciaId(null);
    } else {
      setReferencias((prev) => [...prev, { id: Date.now(), ...updatedRef }]);
    }
    setOpenReferenciaModal(false);
    resetReferenciaModal();
    setReferenciaRequirementError("");
  };

  const handleOpenPadecimientoModal = () => {
    setPadecimientoForm({ nombre: "" });
    setPadecimientoModalError("");
    setOpenPadecimientoModal(true);
  };

  const handleClosePadecimientoModal = () => {
    setOpenPadecimientoModal(false);
    setPadecimientoModalError("");
  };

  const handleAgregarPadecimiento = () => {
    if (isBlank(padecimientoForm.nombre)) {
      setPadecimientoModalError("Campo obligatorio");
      return;
    }
    const exists = padecimientosList.some(
      (item) => item.nombre === padecimientoForm.nombre,
    );
    if (exists) {
      setPadecimientoModalError("Este padecimiento ya fue agregado.");
      return;
    }
    setPadecimientosList((prev) => [
      ...prev,
      { id: Date.now(), nombre: padecimientoForm.nombre },
    ]);
    setOpenPadecimientoModal(false);
    setPadecimientoModalError("");
    setPadecimientoForm({ nombre: "" });
  };

  const resetPpeFamiliarForm = () => {
    setPpeFamiliarForm({
      primerNombre: "",
      segundoNombre: "",
      primerApellido: "",
      segundoApellido: "",
      parentesco: "",
      nacionalidad:
        getNacionalidadGentilicio(hondurasCatalogItem) || "HONDUREÑA",
    });
    setPpeFamiliarError({});
  };

  const resetPpeEmpresaForm = () => {
    setPpeEmpresaForm({
      razonSocial: "",
      rtn: "",
      giroNegocio: "",
      pais: getPaisDescripcion(hondurasCatalogItem) || "HONDURAS",
    });
    setPpeEmpresaError({});
  };

  const handleOpenPpeFamiliarModal = (item = null) => {
    if (item) {
      setPpeFamiliarForm({
        primerNombre: item.primerNombre,
        segundoNombre: item.segundoNombre,
        primerApellido: item.primerApellido,
        segundoApellido: item.segundoApellido,
        parentesco: item.parentesco,
        nacionalidad: item.nacionalidad,
      });
      setEditingPpeFamiliarId(item.id);
    } else {
      resetPpeFamiliarForm();
      setEditingPpeFamiliarId(null);
    }
    setOpenPpeFamiliarModal(true);
  };

  const handleOpenPpeEmpresaModal = (item = null) => {
    if (item) {
      setPpeEmpresaForm({
        razonSocial: item.razonSocial,
        rtn: item.rtn,
        giroNegocio: item.giroNegocio,
        pais: item.pais,
      });
      setEditingPpeEmpresaId(item.id);
    } else {
      resetPpeEmpresaForm();
      setEditingPpeEmpresaId(null);
    }
    setOpenPpeEmpresaModal(true);
  };

  const handleClosePpeFamiliarModal = () => {
    setOpenPpeFamiliarModal(false);
    setPpeFamiliarError({});
    setEditingPpeFamiliarId(null);
  };

  const handleClosePpeEmpresaModal = () => {
    setOpenPpeEmpresaModal(false);
    setPpeEmpresaError({});
    setEditingPpeEmpresaId(null);
  };

  const handlePpeFamiliarChange = (field) => (event) => {
    let value = event?.target?.value ?? "";
    if (
      [
        "primerNombre",
        "segundoNombre",
        "primerApellido",
        "segundoApellido",
      ].includes(field)
    ) {
      value = value.replace(_ONLY_LETTERS, "").toUpperCase();
    }
    setPpeFamiliarForm((prev) => ({ ...prev, [field]: value }));
    setPpeFamiliarError((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handlePpeEmpresaChange = (field) => (event) => {
    let value = event?.target?.value ?? "";
    if (field === "razonSocial") {
      value = value.toUpperCase();
    } else if (field === "rtn") {
      value = value.replace(_ONLY_DIGITS, "");
    }
    setPpeEmpresaForm((prev) => ({ ...prev, [field]: value }));
    setPpeEmpresaError((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleAgregarPpeFamiliar = () => {
    const errors = {};
    if (isBlank(ppeFamiliarForm.primerNombre))
      errors.primerNombre = "Campo obligatorio";
    if (isBlank(ppeFamiliarForm.primerApellido))
      errors.primerApellido = "Campo obligatorio";
    if (isBlank(ppeFamiliarForm.parentesco))
      errors.parentesco = "Campo obligatorio";
    if (isBlank(ppeFamiliarForm.nacionalidad))
      errors.nacionalidad = "Campo obligatorio";
    if (Object.keys(errors).length > 0) {
      setPpeFamiliarError(errors);
      return;
    }
    const updatedFamiliar = {
      primerNombre: ppeFamiliarForm.primerNombre.trim(),
      segundoNombre: ppeFamiliarForm.segundoNombre.trim(),
      primerApellido: ppeFamiliarForm.primerApellido.trim(),
      segundoApellido: ppeFamiliarForm.segundoApellido.trim(),
      parentesco: ppeFamiliarForm.parentesco,
      nacionalidad: ppeFamiliarForm.nacionalidad,
      FKParentescoId: resolveParentescoId(ppeFamiliarForm.parentesco),
      FKNacionalidadId: resolveNacionalidadId(ppeFamiliarForm.nacionalidad),
    };
    if (editingPpeFamiliarId !== null) {
      setPpeFamiliares((prev) =>
        prev.map((f) =>
          f.id === editingPpeFamiliarId ? { ...f, ...updatedFamiliar } : f,
        ),
      );
    } else {
      setPpeFamiliares((prev) => [
        ...prev,
        { id: Date.now(), ...updatedFamiliar },
      ]);
    }
    setOpenPpeFamiliarModal(false);
    resetPpeFamiliarForm();
    setEditingPpeFamiliarId(null);
  };

  const handleAgregarPpeEmpresa = () => {
    const errors = {};
    if (isBlank(ppeEmpresaForm.razonSocial))
      errors.razonSocial = "Campo obligatorio";
    if (isBlank(ppeEmpresaForm.rtn)) errors.rtn = "Campo obligatorio";
    if (isBlank(ppeEmpresaForm.giroNegocio))
      errors.giroNegocio = "Campo obligatorio";
    if (isBlank(ppeEmpresaForm.pais)) errors.pais = "Campo obligatorio";
    if (Object.keys(errors).length > 0) {
      setPpeEmpresaError(errors);
      return;
    }
    const updatedEmpresa = {
      razonSocial: ppeEmpresaForm.razonSocial.trim(),
      rtn: ppeEmpresaForm.rtn.trim(),
      giroNegocio: ppeEmpresaForm.giroNegocio.trim(),
      pais: ppeEmpresaForm.pais,
      FKGiroEntidadId: resolveGiroEntidadId(ppeEmpresaForm.giroNegocio),
      FKNacionalidadId: resolveNacionalidadId(ppeEmpresaForm.pais),
    };
    if (editingPpeEmpresaId !== null) {
      setPpeEmpresas((prev) =>
        prev.map((e) =>
          e.id === editingPpeEmpresaId ? { ...e, ...updatedEmpresa } : e,
        ),
      );
    } else {
      setPpeEmpresas((prev) => [
        ...prev,
        { id: Date.now(), ...updatedEmpresa },
      ]);
    }
    setOpenPpeEmpresaModal(false);
    resetPpeEmpresaForm();
    setEditingPpeEmpresaId(null);
  };

  const handleBeneficiarioFormChange = (field) => (event) => {
    const raw =
      event?.target?.type === "checkbox"
        ? event.target.checked
        : (event?.target?.value ?? "");
    let value = raw;
    if (typeof value === "string") {
      if (_BENEFICIARIO_NAME_FIELDS.has(field)) {
        value = normalizeBeneficiarioNameInput(value);
      } else if (field === "numeroDocumento") {
        value = value.replace(_ONLY_DIGITS, "");
      }
    }
    setBeneficiarioForm((prev) => {
      if (field === "esLegal") {
        return {
          ...prev,
          esLegal: value,
          porcentaje: value ? prev.porcentaje : "",
        };
      }
      return { ...prev, [field]: value };
    });
    setBeneficiarioModalError((prev) => {
      if (!prev[field] && !(field === "esLegal" && prev.porcentaje))
        return prev;
      const next = { ...prev };
      delete next[field];
      if (field === "esLegal") delete next.porcentaje;
      return next;
    });
  };
  const handleBeneficiarioNameBlur = (field) => () => {
    if (!_BENEFICIARIO_NAME_FIELDS.has(field)) return;
    setBeneficiarioForm((prev) => {
      const normalizedValue = normalizeBeneficiarioNameValue(prev[field]);
      if (prev[field] === normalizedValue) return prev;
      return { ...prev, [field]: normalizedValue };
    });
  };

  const resetBeneficiarioModal = () => {
    setBeneficiarioForm({
      primerNombre: "",
      segundoNombre: "",
      primerApellido: "",
      segundoApellido: "",
      numeroDocumento: "",
      parentesco: "",
      esLegal: false,
      porcentaje: "",
    });
    setBeneficiarioModalError({});
  };

  const handleOpenBeneficiarioModal = (item = null) => {
    if (item) {
      setBeneficiarioForm({
        primerNombre: item.primerNombre,
        segundoNombre: item.segundoNombre,
        primerApellido: item.primerApellido,
        segundoApellido: item.segundoApellido,
        numeroDocumento: item.numeroDocumento,
        parentesco: item.parentesco,
        esLegal: item.tipo === "Legal",
        porcentaje: item.tipo === "Legal" ? String(item.porcentaje) : "",
      });
      setEditingBeneficiarioId(item.id);
    } else {
      resetBeneficiarioModal();
      setEditingBeneficiarioId(null);
    }
    setOpenBeneficiarioModal(true);
  };

  const handleCloseBeneficiarioModal = () => {
    setOpenBeneficiarioModal(false);
    setBeneficiarioModalError({});
    setEditingBeneficiarioId(null);
  };

  const handleAgregarBeneficiario = () => {
    const errors = {};
    if (isBlank(beneficiarioForm.primerNombre)) {
      errors.primerNombre = "Campo obligatorio";
    }
    if (isBlank(beneficiarioForm.primerApellido)) {
      errors.primerApellido = "Campo obligatorio";
    }
    if (isBlank(beneficiarioForm.numeroDocumento)) {
      errors.numeroDocumento = "Campo obligatorio";
    }
    if (isBlank(beneficiarioForm.parentesco)) {
      errors.parentesco = "Campo obligatorio";
    }

    if (beneficiarioForm.esLegal) {
      if (form.noDesignacionBeneficiarios) {
        errors.esLegal =
          "Desmarque la opción de no designación para agregar legales.";
      }
      const porcentaje = Number(beneficiarioForm.porcentaje);
      // When editing, add back the current item's porcentaje to available budget
      const currentPorcentaje =
        editingBeneficiarioId !== null
          ? Number(
              beneficiarios.find((b) => b.id === editingBeneficiarioId)
                ?.porcentaje || 0,
            )
          : 0;
      const disponible = porcentajeDisponibleLegal + currentPorcentaje;
      if (isBlank(beneficiarioForm.porcentaje)) {
        errors.porcentaje = "Campo obligatorio";
      } else if (Number.isNaN(porcentaje) || porcentaje <= 0) {
        errors.porcentaje = "Debe ser mayor a 0";
      } else if (porcentaje > disponible) {
        errors.porcentaje = `Solo dispone de ${disponible}%`;
      }
    }

    if (Object.keys(errors).length > 0) {
      setBeneficiarioModalError(errors);
      return;
    }

    const updatedBeneficiario = {
      primerNombre: normalizeBeneficiarioNameValue(
        beneficiarioForm.primerNombre,
      ),
      segundoNombre: normalizeBeneficiarioNameValue(
        beneficiarioForm.segundoNombre,
      ),
      primerApellido: normalizeBeneficiarioNameValue(
        beneficiarioForm.primerApellido,
      ),
      segundoApellido: normalizeBeneficiarioNameValue(
        beneficiarioForm.segundoApellido,
      ),
      numeroDocumento: beneficiarioForm.numeroDocumento.trim(),
      parentesco: beneficiarioForm.parentesco,
      porcentaje: beneficiarioForm.esLegal
        ? Number(beneficiarioForm.porcentaje)
        : 0,
      tipo: beneficiarioForm.esLegal ? "Legal" : "Potencial",
    };
    if (editingBeneficiarioId !== null) {
      setBeneficiarios((prev) =>
        prev.map((b) =>
          b.id === editingBeneficiarioId ? { ...b, ...updatedBeneficiario } : b,
        ),
      );
    } else {
      setBeneficiarios((prev) => [
        ...prev,
        { id: Date.now(), ...updatedBeneficiario },
      ]);
    }
    setOpenBeneficiarioModal(false);
    resetBeneficiarioModal();
    setEditingBeneficiarioId(null);
    setBeneficiariosRequirementError("");
  };

  const handleEliminarBeneficiario = (id) => {
    setBeneficiarios((prev) => prev.filter((item) => item.id !== id));
    setBeneficiariosRequirementError("");
  };

  const buildFichaDocxWithData = async () => {
    const escapeXml = (value) =>
      `${value ?? ""}`
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const formatDate = (value) => {
      if (!value) return "";
      if (value.includes("-") && value.length >= 10) {
        const [yyyy, mm, dd] = value.split("-");
        if (yyyy && mm && dd) return `${dd}-${mm}-${yyyy}`;
      }
      return value;
    };

    const generoLabel =
      form.genero === "M" ? "MASCULINO" : form.genero === "F" ? "FEMENINO" : "";

    const referenciasLaborales = referencias
      .filter((item) => item.tipoReferencia === "LABORALES")
      .slice(0, 2);
    const referenciasPersonales = referencias
      .filter((item) => item.tipoReferencia === "PERSONALES")
      .slice(0, 2);
    const referenciasBancarias = referencias
      .filter((item) => item.tipoReferencia === "COMERCIALES / BANCARIAS")
      .slice(0, 2);
    const beneficiariosPreview = beneficiarios.slice(0, 2);

    const templateBytes = await fetch(fichaTemplateDocx).then((res) =>
      res.arrayBuffer(),
    );
    const zip = await JSZip.loadAsync(templateBytes);
    let xml = await zip.file("word/document.xml").async("string");

    const setAt = (x, y, value) => {
      const text = `${value ?? ""}`.trim();
      let replaced = false;
      xml = xml.replace(/<w:p[\s\S]*?<\/w:p>/g, (paragraph) => {
        if (replaced) return paragraph;

        const frameTag = paragraph.match(/<w:framePr[^>]*>/);
        if (!frameTag) return paragraph;

        const xMatch = frameTag[0].match(/w:x="(\d+)"/);
        const yMatch = frameTag[0].match(/w:y="(\d+)"/);
        if (!xMatch || !yMatch) return paragraph;
        if (
          Number(xMatch[1]) !== Number(x) ||
          Number(yMatch[1]) !== Number(y)
        ) {
          return paragraph;
        }
        replaced = true;
        const safeText = text ? escapeXml(text) : " ";
        let used = false;
        let updated = paragraph.replace(/<w:t[^>]*>[\s\S]*?<\/w:t>/, () => {
          used = true;
          return `<w:t xml:space="preserve">${safeText}</w:t>`;
        });

        if (!used) {
          updated = updated.replace(
            /<\/w:p>$/,
            `<w:r><w:t xml:space="preserve">${safeText}</w:t></w:r></w:p>`,
          );
        }
        return updated;
      });
    };

    const markX = (x, y) => setAt(x, y, "X");

    setAt(845, 2536, form.primerNombre);
    setAt(2651, 2536, form.segundoNombre);
    setAt(4775, 2536, form.primerApellido);
    setAt(7157, 2536, form.segundoApellido);
    setAt(845, 3165, form.tipoDocumento);
    setAt(2651, 3165, formatIdentificacionVisual(form.identificacion));
    setAt(7157, 3165, formatDate(form.fechaNacimiento));
    setAt(845, 3770, form.lugarNacimiento);
    setAt(2656, 3770, generoLabel);
    setAt(4775, 3770, form.nacionalidad);
    setAt(7157, 3770, getEstadoCivilLabel(form.estadoCivil));
    setAt(845, 4321, form.profesion);
    setAt(4775, 4321, formatTelefonoVisual(form.telefono));
    setAt(7157, 4321, formatTelefonoVisual(form.movil));
    setAt(845, 4883, form.correo);
    setAt(845, 5798, form.coloniaResidencia);
    setAt(2651, 5798, form.calle || form.direccionCompleta);
    setAt(4775, 5798, form.numeroCasa || form.numeroCasaTrabajo);
    setAt(7157, 5798, form.municipioResidencia);
    setAt(845, 6413, form.departamentoResidencia);
    setAt(2651, 6413, `${form.tiempoResidir || 0} AÑOS`);
    setAt(4775, 6413, form.referenciaDireccion);
    setAt(845, 7215, form.patrono);
    setAt(2651, 7215, form.coloniaTrabajo);
    setAt(4775, 7215, form.numeroCasaTrabajo);
    setAt(7157, 7215, form.municipioTrabajo);
    setAt(845, 7766, form.departamentoTrabajo);
    setAt(2656, 7766, formatTelefonoVisual(form.telefonoTrabajo));
    setAt(4775, 7766, form.referenciaTrabajo);
    setAt(7157, 7766, form.giroEntidad);
    setAt(845, 8296, form.cargoDesempenado);
    setAt(2651, 8296, form.antiguedadLaboral);
    setAt(4775, 8296, form.ingresoAproxRango);
    setAt(7382, 8296, form.ingresoAproxMonto);

    if (form.tipoVivienda?.toUpperCase().includes("PROPIA")) markX(2817, 11494);
    if (form.tipoVivienda?.toUpperCase().includes("ALQUIL")) markX(4334, 11494);
    if (form.tipoVivienda?.toUpperCase().includes("FAMILIAR"))
      markX(5928, 11494);
    if (form.tipoVivienda?.toUpperCase().includes("HIPOT")) markX(7549, 11494);

    if (form.poseeVehiculo === "Si") {
      markX(2823, 11840);
      setAt(6312, 11840, form.numeroVehiculos);
    } else {
      markX(3802, 11840);
    }

    if (form.poseeInmuebles === "Si") {
      markX(2836, 12240);
      setAt(4614, 12240, form.descOtrosInmuebles);
    } else {
      markX(3815, 12240);
    }

    if (form.otrosIngresos === "Si") {
      markX(2836, 12600);
      setAt(4614, 12600, form.descOtrosIngresos);
      setAt(840, 12974, form.montoOtrosIngresos);
    } else {
      markX(3815, 12600);
    }

    setAt(
      1230,
      1206,
      `${beneficiariosPreview[0]?.primerNombre || ""} ${beneficiariosPreview[0]?.segundoNombre || ""}`,
    );
    setAt(
      3103,
      1206,
      `${beneficiariosPreview[0]?.primerApellido || ""} ${beneficiariosPreview[0]?.segundoApellido || ""}`,
    );
    setAt(
      5364,
      1206,
      formatIdentificacionVisual(beneficiariosPreview[0]?.numeroDocumento),
    );
    setAt(7223, 1206, beneficiariosPreview[0]?.parentesco);
    setAt(
      1230,
      1446,
      `${beneficiariosPreview[1]?.primerNombre || ""} ${beneficiariosPreview[1]?.segundoNombre || ""}`,
    );
    setAt(
      3103,
      1446,
      `${beneficiariosPreview[1]?.primerApellido || ""} ${beneficiariosPreview[1]?.segundoApellido || ""}`,
    );
    setAt(
      5364,
      1446,
      formatIdentificacionVisual(beneficiariosPreview[1]?.numeroDocumento),
    );
    setAt(7223, 1446, beneficiariosPreview[1]?.parentesco);

    setAt(872, 13873, referenciasLaborales[0]?.nombreCompleto);
    setAt(3781, 13873, referenciasLaborales[0]?.direccion);
    setAt(6707, 13873, formatTelefonoVisual(referenciasLaborales[0]?.telefono));
    setAt(872, 14113, referenciasLaborales[1]?.nombreCompleto);
    setAt(3781, 14113, referenciasLaborales[1]?.direccion);
    setAt(6707, 14113, formatTelefonoVisual(referenciasLaborales[1]?.telefono));

    setAt(864, 15703, referenciasPersonales[0]?.nombreCompleto);
    setAt(3773, 15703, referenciasPersonales[0]?.direccion);
    setAt(
      6699,
      15703,
      formatTelefonoVisual(referenciasPersonales[0]?.telefono),
    );
    setAt(864, 15943, referenciasPersonales[1]?.nombreCompleto);
    setAt(3773, 15943, referenciasPersonales[1]?.direccion);
    setAt(
      6699,
      15943,
      formatTelefonoVisual(referenciasPersonales[1]?.telefono),
    );

    setAt(864, 17455, referenciasBancarias[0]?.nombreCompleto);
    setAt(6186, 17455, formatTelefonoVisual(referenciasBancarias[0]?.telefono));
    setAt(864, 17695, referenciasBancarias[1]?.nombreCompleto);
    setAt(6186, 17695, formatTelefonoVisual(referenciasBancarias[1]?.telefono));

    const esPepMarcadoDocx =
      form.esPep === true ||
      form.esPep === 1 ||
      form.esPep === "1" ||
      ["SI", "SÍ", "YES", "TRUE", "T"].includes(
        `${form.esPep ?? ""}`.trim().toUpperCase(),
      );
    if (esPepMarcadoDocx) markX(4128, 5823);
    else markX(5107, 5823);
    if (form.noDesignacionBeneficiarios) markX(847, 3479);

    zip.file("word/document.xml", xml);
    return zip.generateAsync({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });
  };

  const handleOpenFichaPreview = () => {
    const toSafe = (value) =>
      `${value ?? ""}`
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const formatDate = (value) => {
      if (!value) return "";
      if (value.includes("-") && value.length >= 10) {
        const [yyyy, mm, dd] = value.split("-");
        return `${dd}-${mm}-${yyyy}`;
      }
      return value;
    };

    const nombreCompleto = [
      form.primerNombre,
      form.segundoNombre,
      form.primerApellido,
      form.segundoApellido,
    ]
      .filter(Boolean)
      .join(" ");
    const generoLabel =
      form.genero === "M" ? "MASCULINO" : form.genero === "F" ? "FEMENINO" : "";
    const edadAnios =
      form.fechaNacimiento &&
      !Number.isNaN(new Date(form.fechaNacimiento).getTime())
        ? Math.max(
            0,
            new Date().getFullYear() -
              new Date(form.fechaNacimiento).getFullYear(),
          )
        : "";
    const esPepMarcado =
      form.esPep === true ||
      form.esPep === 1 ||
      form.esPep === "1" ||
      ["SI", "SÍ", "YES", "TRUE", "T"].includes(
        `${form.esPep ?? ""}`.trim().toUpperCase(),
      );

    const laborales = referencias.filter(
      (item) => item.tipoReferencia === "LABORALES",
    );
    const personales = referencias.filter(
      (item) => item.tipoReferencia === "PERSONALES",
    );
    const bancarias = referencias.filter(
      (item) => item.tipoReferencia === "COMERCIALES / BANCARIAS",
    );
    const refsRows = (list) =>
      (list.length ? list : [{}]).map(
        (item) => `
        <tr>
          <td>${toSafe(item.nombreCompleto)}</td>
          <td>${toSafe(item.direccion)}</td>
          <td>${toSafe(formatTelefonoVisual(item.telefono))}</td>
        </tr>
      `,
      );

    const beneficiariosPotenciales = beneficiarios.filter(
      (item) => item.tipo === "Potencial",
    );
    const beneficiariosRows = (
      beneficiariosPotenciales.length ? beneficiariosPotenciales : [{}]
    ).map(
      (item, index) => `
        <tr>
          <td class="cell-center">${item.id ? index + 1 : ""}</td>
          <td>${toSafe([item.primerNombre, item.segundoNombre].filter(Boolean).join(" "))}</td>
          <td>${toSafe([item.primerApellido, item.segundoApellido].filter(Boolean).join(" "))}</td>
          <td>${toSafe(formatIdentificacionVisual(item.numeroDocumento))}</td>
          <td>${toSafe(item.parentesco)}</td>
        </tr>
      `,
    );

    const cargosRows = (otrosCargos.length ? otrosCargos : [{}]).map(
      (item, index) => `
        <tr>
          <td>${item.id ? index + 1 : ""}</td>
          <td>${toSafe(item.empleadorPatrono)}</td>
          <td>${toSafe(item.cargoDesempenado)}</td>
          <td>${toSafe(item.actividadGiro)}</td>
        </tr>
      `,
    );

    const nombreOficinaFirma = toSafe(
      resolverNombreOficina(sessi?.sessionData?.oficina),
    );
    const _now = new Date();
    const MESES = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];
    const fechaLarga = `${_now.getDate()} días de ${MESES[_now.getMonth()]} de ${_now.getFullYear()}`;
    const horaLarga = _now.toLocaleTimeString("es-HN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const html = `
      <!doctype html>
      <html lang="es">
      <head>
        <meta charset="utf-8" />
        <title>Ficha de Afiliacion</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; margin: 0; background: #AD8401; color: #111; font-size: 11px; }
          .toolbar { position: sticky; top: 0; z-index: 10; background: #fff; padding: 10px 14px; border-bottom: 1px solid #ccc; display: flex; gap: 8px; }
          .toolbar button { padding: 8px 14px; border: 1px solid #888; background: #fff; cursor: pointer; font-weight: 700; }
          .page { width: 216mm; margin: 8px auto; background: #fff; padding: 6mm; box-sizing: border-box; }
          .page + .page { margin-top: 4mm; }
          .page-break { page-break-after: always; break-after: page; }
          .header { display: grid; grid-template-columns: 50% 50%; align-items: center; border: 1px solid #111; border-bottom: none; }
          .header img { height: 64px; max-width: 100%; object-fit: contain; margin: 6px 8px; }
          .title { text-align: center; font-size: 22px; font-weight: 700; }
          .section-title { margin: 4px 0 0; padding: 5px 7px; background: #efefef; border: 1px solid #111; font-size: 12px; font-weight: 700; text-transform: uppercase; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 11px; }
          th, td { border: 1px solid #111; padding: 3px 5px; vertical-align: top; word-wrap: break-word; line-height: 1.25; }
          th { background: #f6f6f6; text-align: left; font-weight: 700; }
          .linea-firma { border-top: 1px solid #111; margin-top: 24px; padding-top: 4px; text-align: center; font-size: 10px; }
          .firma { margin-top: 8px; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
          .cell-center { text-align: center; }
          .legal-title { margin: 6px 0 0; padding: 5px; background: #dedede; border: 1px solid #111; font-size: 12px; font-weight: 700; text-align: center; text-transform: uppercase; }
          .legal-box { border: 1px solid #111; border-top: none; padding: 6px; font-size: 11px; line-height: 1.3; }
          .mini-row { display: grid; grid-template-columns: 86px 1fr; border: 1px solid #111; border-top: none; }
          .mini-row .left { border-right: 1px solid #111; font-size: 10px; font-weight: 700; padding: 4px; text-align: center; text-transform: uppercase; line-height: 1.15; word-break: normal; }
          .mini-row .right { padding: 5px 7px; font-size: 11px; line-height: 1.3; }
          .check-line { display: flex; align-items: center; gap: 20px; padding: 5px 7px; font-size: 11px; }
          .check { display: inline-block; width: 11px; height: 11px; border: 1px solid #111; margin-right: 4px; vertical-align: middle; }
          .fill-line { display: inline-block; border-bottom: 1px solid #111; min-width: 80px; height: 12px; vertical-align: middle; }
          .footer-signatures { margin-top: 24px; display: grid; grid-template-columns: 1fr 1fr; gap: 60px; }
          .footer-signatures .line { border-top: 1px solid #111; padding-top: 3px; text-align: center; font-size: 10px; }
          tr { page-break-inside: avoid; break-inside: avoid; }
          table { page-break-inside: auto; break-inside: auto; }
          .compact-section { margin-top: 6px; }
          @page { size: legal portrait; margin: 8mm; }
          @media print {
            body { background: #fff; }
            .toolbar { display: none; }
            .page { margin: 0; width: auto; min-height: 0; padding: 5mm 3mm; }
            .page + .page { margin-top: 0; }
            .page-break { page-break-after: auto; break-after: auto; }
            .second-page { page-break-before: always; break-before: page; }
          }
        </style>
      </head>
      <body>
        <div class="toolbar">
          <button onclick="window.print()">Imprimir</button>
          <button onclick="window.close()">Cerrar</button>
        </div>
        <div class="page">
          <div class="header">
            <div><img src="${injupempGobiernoLogo}" alt="Logo" /></div>
            <div class="title">FORMULARIO DE AFILIACIÓN</div>
          </div>

          <div class="section-title">I. DATOS DEL SOLICITANTE</div>
          <table>
            <tr><th>Primer Nombre</th><th>Segundo Nombre</th><th>Primer Apellido</th><th>Segundo Apellido</th></tr>
            <tr><td>${toSafe(form.primerNombre)}</td><td>${toSafe(form.segundoNombre)}</td><td>${toSafe(form.primerApellido)}</td><td>${toSafe(form.segundoApellido)}</td></tr>
            <tr><th>Tipo de Identificación</th><th>N° de Documento de Identificación</th><th>Fecha de Nacimiento</th><th></th></tr>
            <tr><td>${toSafe(form.tipoDocumento)}</td><td>${toSafe(formatIdentificacionVisual(form.identificacion))}</td><td>${toSafe(formatDate(form.fechaNacimiento))}</td><td></td></tr>
            <tr><th>Lugar de Nacimiento</th><th>Género</th><th>Nacionalidad</th><th>Estado Civil</th></tr>
            <tr><td>${toSafe(form.lugarNacimiento)}</td><td>${toSafe(generoLabel)}</td><td>${toSafe(form.nacionalidad)}</td><td>${toSafe(getEstadoCivilLabel(form.estadoCivil))}</td></tr>
            <tr><th>Profesión u Oficio / Actividad Económica</th><th>Número de Teléfono Fijo</th><th>Número de Teléfono Celular</th><th></th></tr>
            <tr><td>${toSafe(form.profesion)}</td><td>${toSafe(formatTelefonoVisual(form.telefono))}</td><td>${toSafe(formatTelefonoVisual(form.movil))}</td><td></td></tr>
            <tr><th>Correo Electrónico</th><th>Nombre del Conyuge</th><th></th><th></th></tr>
            <tr><td>${toSafe(noCorreo ? "" : form.correo)}</td><td>${toSafe(form.nombreConyuge)}</td><td></td><td></td></tr>
          </table>

          <div class="section-title compact-section">II. INFORMACIÓN RESIDENCIAL</div>
          <table>
            <tr><th>Barrio o Colonia</th><th>Calle o Avenida</th><th>N° de Casa</th><th>Municipio</th></tr>
            <tr><td>${toSafe(form.coloniaResidencia)}</td><td>${toSafe(form.calle)}</td><td>${toSafe(form.numeroCasa)}</td><td>${toSafe(form.municipioResidencia)}</td></tr>
            <tr><th>Departamento</th><th>Tiempo de Residir</th><th colspan="2">Referencia de Domicilio</th></tr>
            <tr><td>${toSafe(form.departamentoResidencia)}</td><td>${toSafe(form.tiempoResidir)}</td><td colspan="2">${toSafe(form.referenciaDireccion)}</td></tr>
          </table>

          <div class="section-title compact-section">III. INFORMACIÓN LABORAL</div>
          <table>
            <tr><th>Empleador / Patrono</th><th>Barrio o Colonia</th><th>N° de Casa</th><th>Municipio</th></tr>
            <tr><td>${toSafe(form.patrono)}</td><td>${toSafe(form.coloniaTrabajo)}</td><td>${toSafe(form.numeroCasaTrabajo)}</td><td>${toSafe(form.municipioTrabajo)}</td></tr>
            <tr><th>Departamento</th><th>Número de Teléfono</th><th>Referencia de Ubicación</th><th>Giro de Entidad</th></tr>
            <tr><td>${toSafe(form.departamentoTrabajo)}</td><td>${toSafe(formatTelefonoVisual(form.telefonoTrabajo))}</td><td>${toSafe(form.referenciaTrabajo)}</td><td>${toSafe(form.giroEntidad)}</td></tr>
            <tr><th>Cargo desempeñado</th><th>Antigüedad Laboral</th><th>Ingresos Aproximados (miles de L.)</th><th>Ingresos Aproximados (monto)</th></tr>
            <tr><td>${toSafe(form.cargoDesempenado)}</td><td>${toSafe(form.antiguedadLaboral)}</td><td>${toSafe(form.ingresoAproxRango)}</td><td>${toSafe(form.ingresoAproxMonto)}</td></tr>  
          </table>

          <div class="section-title compact-section">OTROS CARGOS PUBLICOS DESEMPEÑADOS EN LOS ULTIMOS CUATRO (4) AÑOS</div>
          <table>
            <tr><th style="width:7%;">No</th><th>Empleador / Patrono</th><th>Cargo Desempeñado</th><th>Actividad o Giro de la Empresa</th></tr>
            ${cargosRows.join("")}
          </table>

          <div class="section-title compact-section">IV. PATRIMONIO</div>
          <table>
            <tr>
              <th>Vivienda</th>
              <td>Propia ${form.tipoVivienda === "PROPIA" ? "X" : ""}</td>
              <td>Alquilada ${form.tipoVivienda === "ALQUILADA" ? "X" : ""}</td>
              <td>Familiar ${form.tipoVivienda === "FAMILIAR" ? "X" : ""}</td>
              <td>Hipoteca ${form.tipoVivienda === "HIPOTECA" ? "X" : ""}</td>
            </tr>
            <tr>
              <th>Posee Vehículo</th>
              <td>Si ${form.poseeVehiculo === "Si" ? "X" : ""}</td>
              <td>No ${form.poseeVehiculo === "No" ? "X" : ""}</td>
              <th>Número de Vehículos</th>
              <td>${form.poseeVehiculo === "Si" ? toSafe(form.numeroVehiculos) : ""}</td>
            </tr>
            <tr>
              <th>Otros Inmuebles</th>
              <td>Si ${form.poseeInmuebles === "Si" ? "X" : ""}</td>
              <td>No ${form.poseeInmuebles === "No" ? "X" : ""}</td>
              <th>Describa</th>
              <td>${form.poseeInmuebles === "Si" ? toSafe(form.descOtrosInmuebles) : ""}</td>
            </tr>
            <tr>
              <th>Otros Ingresos</th>
              <td>Si ${form.otrosIngresos === "Si" ? "X" : ""}</td>
              <td>No ${form.otrosIngresos === "No" ? "X" : ""}</td>
              <th>Origen de Otros Ingresos</th>
              <td>${form.otrosIngresos === "Si" ? toSafe(form.descOtrosIngresos) : ""}</td>
            </tr>
            <tr>
              <th colspan="3">Monto Aproximado de Otros Ingresos (mensuales)</th>
              <td colspan="2">${form.otrosIngresos === "Si" ? toSafe(form.montoOtrosIngresos) : ""}</td>
            </tr>
          </table>

          <div class="section-title compact-section">V. REFERENCIAS LABORALES</div>
          <table>
            <tr><th>Nombre Completo</th><th>Dirección</th><th>N° de Teléfonos</th></tr>
            ${refsRows(laborales).join("")}
          </table>

          <div class="section-title compact-section">VI. REFERENCIAS PERSONALES</div>
          <table>
            <tr><th>Nombre Completo</th><th>Dirección</th><th>N° de Teléfonos</th></tr>
            ${refsRows(personales).join("")}
          </table>

          <div class="section-title compact-section">VII. REFERENCIAS BANCARIAS</div>
          <table>
            <tr><th>Institución Bancaria o Comercial</th><th>N° de Teléfonos</th></tr>
            ${(bancarias.length ? bancarias : [{}])
              .map(
                (item) => `
              <tr>
                <td>${toSafe(item.nombreCompleto)}</td>
                <td>${toSafe(formatTelefonoVisual(item.telefono))}</td>
              </tr>
            `,
              )
              .join("")}
          </table>

        </div>

        <div class="page second-page">
          <div class="section-title">VIII. BENEFICIARIOS</div>
          <table>
            <tr><th style="width:7%;">No</th><th>Nombres</th><th>Apellidos</th><th>N° de Documento</th><th>Parentesco</th></tr>
            ${beneficiariosRows.join("")}
          </table>

          <div class="legal-title">XI. Persona expuesta politicamente</div>
          <div class="legal-box">
            <b>DEFINICION:</b> Las Personas Expuestas Politicamente son aquellas que desempeñan o han desempeñado
            funciones publicas destacadas en un pais extranjero o en su propio pais, por ejemplo jefes de Estado, o de
            un gobierno, politicos de alta jerarquia, funcionarios gubernamentales, judiciales o militares de alta
            jerarquia, altos ejecutivos de empresas estatales, funcionarios importantes de partidos politicos. Ej.
            Presidentes de Estado, Titulares de Poderes de Estado e Instituciones Publicas, Alcaldes, Vicealcaldes,
            Regidores, Diputados, Magistrados de la Corte Suprema de Justicia, Miembros Tribunal de Estado Mayor
            Conjunto, entre otros.
          </div>
          <div class="check-line" style="border:1px solid #111; border-top:none;">
            <span>¿Es usted una Persona Expuesta Politicamente?</span>
            <span><span class="check">${esPepMarcado ? "X" : ""}</span>&nbsp;Si</span>
            <span><span class="check">${!esPepMarcado ? "X" : ""}</span>&nbsp;No</span>
          </div>
          <div class="mini-row">
            <div class="left">Proposito / uso de la cuenta:</div>
            <div class="right">Ingreso de mis Cotizaciones y Aportaciones</div>
          </div>

          <div class="mini-row">
            <div class="left">X. Declaracion</div>
            <div class="right">
              Asi mismo, hago constar que:
              <br />
              1. La informacion detallada en la presente solicitud es verdadera y los documentos adjuntos han sido
              extendidos por autoridad competente.
              <br />
              2. Autorizo al INJUPEMP a verificar los datos e informacion declarada en la presente solicitud.
            </div>
          </div>

          <div class="mini-row">
            <div class="left">XI. Cumplimiento requisitos</div>
            <div class="right">
              Acompaño a esta solicitud con la documentacion siguiente:
              <br />
              a) Acuerdos de Nombramiento firmada y sellada por las Oficinas de Recursos Humanos
              <span style="float:right"><span class="check"></span></span>
              <br />
              b) Una(1) fotocopia legible de la Tarjeta de Identidad
              <span style="float:right"><span class="check"></span></span>
              <br />
              c) Otros documentos acreditados: detalle:
              <span class="fill-line" style="min-width:260px;"></span>
              <span style="float:right"><span class="check"></span></span>
            </div>
          </div>

          <div class="legal-box" style="margin-top:8px;">
            <b>FUNDAMENTOS DE DERECHO:</b> Fundamento mi petición en el Articulo 80 de la Constitución de la Republica,
            Articulo 2, numeral 1 y 4 de la Ley de INJUPEMP.
          </div>
          <div class="legal-box" style="margin-top:6px;">
            <b>NOTIFICACION:</b>
            <br />
            - Esta solicitud deberá ser presentada personalmente
            <br />
            - Para efectos de notificación, el INJUPEMP utilizará el correo electrónico designado en la presente solicitud.
          </div>
          <p>Firmo en ${nombreOficinaFirma}, a los ${fechaLarga}, siendo las ${horaLarga}.</p>

          <div style="margin-top:70px; text-align:center;">
            <div style="width:320px; margin:0 auto; border-top:1px solid #111; padding-top:4px; text-align:center; font-weight:700; font-size:11px;">
              FIRMA Y HUELLA DEL SOLICITANTE
            </div>
          </div>

          <div class="legal-title" style="margin-top:16px;">XII. Para uso exclusivo del INJUPEMP</div>
          <div style="padding:6px 0 0; font-size:10px;">
            <b>Estatus del Participante:</b>
            <span style="margin-left:24px;"><span class="check"></span>Activo</span>
            <span style="margin-left:24px;"><span class="check"></span>Voluntario</span>
            <span style="margin-left:24px;"><span class="check"></span>Suspenso</span>
          </div>
          <br><br><br><br><br><br><br>

          <div class="footer-signatures">
            <div class="line">Nombre del Auxiliar de Registro y Afiliación</div>
            <div class="line">Firma</div>
          </div>
        </div>
      </body>
      </html>
    `;

    const popup = window.open(
      "",
      "ficha_preview_popup",
      "width=1200,height=900",
    );
    if (!popup) {
      setSaveFichaError(
        "No se pudo abrir la vista previa. Habilite popups del navegador.",
      );
      return;
    }
    popup.document.open();
    popup.document.write(html);
    popup.document.close();
    setSaveFichaError("");
  };

  const handleAdjuntarFicha = (event) => {
    const file = event.target.files?.[0] || null;
    setFichaAdjunta(file);
    setSaveFichaError("");
    setSaveFichaSuccess("");
  };

  const handleAdjuntarFichaPep = (event) => {
    const file = event.target.files?.[0] || null;
    setFichaAdjuntaPep(file);
    setSaveFichaError("");
    setSaveFichaSuccess("");
  };

  const handleOpenFichaPepPreview = () => {
    const toSafe = (v) =>
      String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

    const formatShortDate = (value) => {
      if (!value) return "";
      if (value.includes("-") && value.length >= 10) {
        const [yyyy, mm, dd] = value.split("-");
        return `${dd}-${mm}-${yyyy}`;
      }
      return value;
    };

    const formatFechaImpresion = () => {
      const date = new Date();
      const meses = [
        "enero",
        "febrero",
        "marzo",
        "abril",
        "mayo",
        "junio",
        "julio",
        "agosto",
        "septiembre",
        "octubre",
        "noviembre",
        "diciembre",
      ];
      const dd = String(date.getDate()).padStart(2, "0");
      const mes = meses[date.getMonth()];
      const yyyy = date.getFullYear();
      const mins = String(date.getMinutes()).padStart(2, "0");
      const rawHours = date.getHours();
      const ampm = rawHours >= 12 ? "p. m." : "a. m.";
      const hh = rawHours % 12 || 12;
      return `Fecha: ${dd} de ${mes} de ${yyyy}, ${hh}:${mins} ${ampm}.`;
    };

    const nombreCompleto = [
      form.primerNombre,
      form.segundoNombre,
      form.primerApellido,
      form.segundoApellido,
    ]
      .filter(Boolean)
      .join(" ");

    const familiaresData = [...(ppeFamiliares || [])];
    while (familiaresData.length < 4) familiaresData.push({});
    const familiaresRows = familiaresData
      .map(
        (f) => `
          <tr>
            <td>${toSafe([f.primerNombre, f.segundoNombre, f.primerApellido, f.segundoApellido].filter(Boolean).join(" "))}</td>
            <td>${toSafe(f.parentesco)}</td>
            <td>${toSafe(f.nacionalidad)}</td>
          </tr>`,
      )
      .join("");

    const empresasData = [...(ppeEmpresas || [])];
    while (empresasData.length < 3) empresasData.push({});
    const empresasRows = empresasData
      .map(
        (e) => `
          <tr>
            <td>${toSafe(e.razonSocial)}</td>
            <td>${toSafe(e.rtn)}</td>
            <td>${toSafe(e.pais)}</td>
            <td>${toSafe(e.giroNegocio)}</td>
          </tr>`,
      )
      .join("");

    const fechaImpresion = formatFechaImpresion();
    const institucionPublica = form.patrono || form.direccionTrabajo || "";
    const cargoPublico = form.cargoDesempenado || form.areaTrabajo || "";

    const pepHtml = `
      <!doctype html>
      <html lang="es">
      <head>
        <meta charset="utf-8" />
        <title>Ficha PEP - Persona Politicamente Expuesta</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; margin: 0; background: #fff; color: #111; font-size: 11px; }
          .toolbar { position: sticky; top: 0; z-index: 10; background: #fff; padding: 10px 14px; border-bottom: 1px solid #ccc; display: flex; gap: 8px; }
          .toolbar button { padding: 8px 14px; border: 1px solid #888; background: #fff; cursor: pointer; font-weight: 700; }
          .page { width: 215.9mm; min-height: 355.6mm; margin: 0 auto; background: #fff; padding: 12mm 15mm; }
          .header { display: flex; align-items: center; gap: 14px; border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 10px; }
          .header-logo { flex: 0 0 auto; }
          .header-logo img { height: 22mm; width: auto; display: block; }
          .header-title { flex: 1; text-align: right; }
          .title { font-size: 13px; font-weight: 700; text-transform: uppercase; }
          .subtitle { font-size: 13px; font-weight: 700; text-transform: uppercase; margin-top: 3px; }
          .intro { margin: 10px 0 12px; font-size: 10px; line-height: 1.45; text-align: justify; }
          .section { margin-top: 10px; padding: 5px 10px; background: #1f4e79; color: #fff; font-size: 10px; font-weight: 700; text-align: center; text-transform: none; }
          .form-table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 0; }
          .form-table td { border: 1px solid #bfbfbf; padding: 5px 7px; font-size: 10.5px; text-align: center; text-transform: uppercase; }
          .form-table .values td { font-weight: 700; border-bottom: none; }
          .form-table .labels td { border-top: none; font-size: 9px; color: #1f4e79; text-transform: none; padding-top: 2px; padding-bottom: 5px; font-style: italic; }
          .form-table.four td { width: 25%; }
          .form-table.two td { width: 50%; }
          .data-table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 4px; font-size: 10px; }
          .data-table th, .data-table td { border: 1px solid #bfbfbf; padding: 4px 6px; text-align: center; text-transform: uppercase; }
          .data-table td { min-height: 18px; height: 18px; }
          .data-table th { background: #d9d9d9; font-weight: 700; color: #1f4e79; text-transform: none; }
          .note { font-size: 9.5px; margin: 8px 0 3px; line-height: 1.35; }
          .cert { margin-top: 48px; font-size: 10px; line-height: 1.45; text-align: justify; }
          .date-line { margin-top: 10px; font-size: 10px; }
          .signature { margin-top: 100px; text-align: center; font-size: 10px; }
          .signature span { display: inline-block; min-width: 260px; border-top: 1px solid #111; padding-top: 4px; font-weight: 700; }
          @page { size: legal portrait; margin: 12mm; }
          @media print {
            body { background: #fff; }
            .toolbar { display: none; }
            .page { margin: 0; width: auto; padding: 8mm 10mm; }
          }
        </style>
      </head>
      <body>
        <div class="toolbar">
          <button onclick="window.print()">Imprimir</button>
          <button onclick="window.close()">Cerrar</button>
        </div>
        <div class="page">
          <div class="header">
            <div class="header-logo"><img src="${injupempGobiernoLogo}" alt="Logo INJUPEMP" /></div>
            <div class="header-title">
              <div class="title">FORMULARIO DE IDENTIFICACION</div>
              <div class="subtitle">PERSONAS EXPUESTAS POLITICAMENTE (PEP)</div>
            </div>
          </div>

          <p class="intro">
            Las personas expuestas políticamente se definen como aquellas que desempeñan o han desempeñado funciones
            públicas destacadas en el país o en un país extranjero, los nacionales o extranjeros a quienes una organización
            internacional les ha confiado una función destacada dentro o fuera del país (Resolución CNBS SB No. 348/27-04-2016).
          </p>

          <div class="section">1. Datos Generales del Participanete / Representante</div>
          <table class="form-table four">
            <tr class="values">
              <td>${toSafe(form.primerNombre)}</td>
              <td>${toSafe(form.segundoNombre)}</td>
              <td>${toSafe(form.primerApellido)}</td>
              <td>${toSafe(form.segundoApellido)}</td>
            </tr>
            <tr class="labels">
              <td>Primer Nombre</td>
              <td>Segundo Nombre</td>
              <td>Primer Apellido</td>
              <td>Segundo Apellido</td>
            </tr>
          </table>

          <table class="form-table two">
            <tr class="values">
              <td>${toSafe(formatIdentificacionVisual(form.identificacion))}</td>
              <td>${toSafe(institucionPublica)}</td>
            </tr>
            <tr class="labels">
              <td>Número de Identidad</td>
              <td>Institución Pública donde labora</td>
            </tr>
          </table>

          <table class="form-table two">
            <tr class="values">
              <td>${toSafe(formatShortDate(form.fechaIngresoInstitucion))}</td>
              <td>${toSafe(cargoPublico)}</td>
            </tr>
            <tr class="labels">
              <td>Fecha de Nombramiento</td>
              <td>Cargo que desempeña</td>
            </tr>
          </table>

          <div class="section">2. Datos Familiares</div>
          <p class="note">
            Favor indicar nombres de familiares hasta un segundo grado de consanguinidad y afinidad: Conyuge(1), Hijos (2), Padres
            (3), Hermanos (4), Nietos (5), Nuera/Yerno (6), Abuelos (7)
          </p>
          <table class="data-table">
            <tr>
              <th>Nombres</th>
              <th>Vínculo</th>
              <th>Nacionalidad</th>
            </tr>
            ${familiaresRows}
          </table>

          <p class="note" style="margin-top:10px;">
            Si aplica, relación de empresas donde su persona mantenga más del 25% de participación en el patrimonio.
          </p>
          <table class="data-table">
            <tr>
              <th>Razón Social</th>
              <th>RTN</th>
              <th>País</th>
              <th>Giro del Negocio</th>
            </tr>
            ${empresasRows}
          </table>

          <div class="cert">
            El presente documento certifica que la información ha sido revisada y otorgada por el participante al INJUPEMP
            quedando facultado a utilizarla y se compromete a guardar la debida reserva, confidencialidad y contenido de la misma,
            en conformidad con la legislación aplicable.
          </div>

          <div class="date-line">${toSafe(fechaImpresion)}</div>

          <div class="signature"><span>Firma del Participante</span></div>
        </div>
      </body>
      </html>
    `;

    const popup = window.open("", "ficha_pep_popup", "width=1200,height=900");
    if (!popup) {
      setSaveFichaError(
        "No se pudo abrir la vista previa. Habilite popups del navegador.",
      );
      return;
    }
    popup.document.open();
    popup.document.write(pepHtml);
    popup.document.close();
    setSaveFichaError("");
  };

  const handleGuardarFinal = async () => {
    if (!fichaAdjunta) {
      setSaveFichaError("Debe adjuntar la ficha firmada para guardar.");
      setSaveFichaSuccess("");
      return;
    }
    if (form.esPep && !fichaAdjuntaPep) {
      setSaveFichaError(
        "Debe adjuntar la ficha de Persona Políticamente Expuesta para guardar.",
      );
      setSaveFichaSuccess("");
      return;
    }

    try {
      setSavingRegistro(true);
      const payload = buildActualizarPayload({
        form: {
          ...form,
          personaIdInterno: form.personaIdInterno || form.identificacion,
          correo: noCorreo ? "" : form.correo,
        },
        referencias,
        beneficiarios: (beneficiarios || []).map((item) => ({
          dni: item?.numeroDocumento || "",
          primerNombre: item?.primerNombre || "",
          segundoNombre: item?.segundoNombre || "",
          primerApellido: item?.primerApellido || "",
          segundoApellido: item?.segundoApellido || "",
          parentesco: item?.parentesco || "",
          tipoDocumento: form.tipoDocumento || "IDENTIDAD",
          porcentaje:
            item?.tipo === "Legal" ? Number(item?.porcentaje || 0) : 0,
          tipo: item?.tipo || "Potencial",
          esLegal: item?.tipo === "Legal",
        })),
        padecimientos: (padecimientosList || []).map((item) => ({
          descripcion: item?.nombre || "",
        })),
        cargosPublicos: otrosCargos || [],
        empresasPpe: ppeEmpresas || [],
        familiaresPpe: ppeFamiliares || [],
      });

      // Incluir foto capturada como base64 para guardar en afiliacion.Fotografias
      if (photoDataUrl) {
        payload.persona.foto_base64 = photoDataUrl;
      }

      // Datos de auditoría del usuario que realiza el registro
      payload.persona.userIdRegistro = sessi?.sessionData?.id ?? null;
      payload.persona.FKUsuarioIdCrea = null;
      payload.persona.FKOficinaRegistroId = null;
      payload.persona.oficinaIdRegistro = sessi?.sessionData?.oficina
        ? Number(sessi.sessionData.oficina)
        : null;

      const saveResponse = await apiRequest("/Enrolamiento/personas/guardar/", {
        authToken,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // Subir adjuntos después de guardar (requieren multipart/form-data)
      const personaIdGuardado =
        saveResponse?.persona?.PersonaId ||
        form.personaIdInterno ||
        form.identificacion;
      if (personaIdGuardado && fichaAdjunta) {
        const fd = new FormData();
        fd.append("archivo", fichaAdjunta);
        fd.append("nombre", fichaAdjunta.name);
        fd.append("descripcion", "Ficha de afiliación firmada");
        fd.append("observaciones", "Ficha de afiliación firmada");
        fd.append("entidad", "AFILIACION");
        await apiRequest(
          `/Enrolamiento/personas/${encodeURIComponent(personaIdGuardado)}/adjuntos/?entidad=afiliacion`,
          { authToken, method: "POST", body: fd },
        );
      }
      if (personaIdGuardado && form.esPep && fichaAdjuntaPep) {
        const fdPep = new FormData();
        fdPep.append("archivo", fichaAdjuntaPep);
        fdPep.append("nombre", fichaAdjuntaPep.name);
        fdPep.append("descripcion", "Ficha PEP firmada");
        fdPep.append("observaciones", "Ficha PEP firmada");
        fdPep.append("entidad", "PEP");
        await apiRequest(
          `/Enrolamiento/personas/${encodeURIComponent(personaIdGuardado)}/adjuntos/?entidad=afiliacion`,
          { authToken, method: "POST", body: fdPep },
        );
      }

      // Éxito: mostrar diálogo y luego resetear
      const nombreGuardado = [
        form.primerNombre,
        form.segundoNombre,
        form.primerApellido,
        form.segundoApellido,
      ]
        .filter(Boolean)
        .join(" ");
      setSavedPersonaName(nombreGuardado);
      setSuccessOpen(true);
    } catch (error) {
      setSaveFichaSuccess("");
      setSaveFichaError(error?.message || "No se pudo guardar el registro.");
    } finally {
      setSavingRegistro(false);
    }
  };

  const handleCloseSuccess = () => {
    setSuccessOpen(false);
    setStep(0);
    setTab(0);
    setForm({
      tipoDocumento: "IDENTIDAD",
      identificacion: "",
      primerNombre: "",
      segundoNombre: "",
      primerApellido: "",
      segundoApellido: "",
      genero: "",
      paisNacimiento: "HONDURAS",
      nacionalidad: "HONDUREÑA",
      fechaNacimiento: "",
      lugarNacimiento: "",
      estadoCivil: "",
      departamentoResidencia: "",
      municipioResidencia: "",
      coloniaResidencia: "",
      direccionCompleta: "",
      calle: "",
      numeroCasa: "",
      referenciaDireccion: "",
      tiempoResidir: 0,
      fechaDesdeReside: "",
      telefono: "",
      movil: "",
      correo: "",
      tipoCliente: "",
      rtn: "",
      profesion: "",
      actividadEconomicaCnbs: "",
      profesionCnbs: "",
      nivelEducativo: "",
      nombreConyuge: "",
      tipoDocumentoConyuge: "",
      identificacionConyuge: "",
      generoConyuge: "",
      fechaNacimientoConyuge: "",
      patrono: "",
      departamentoTrabajo: "",
      municipioTrabajo: "",
      coloniaTrabajo: "",
      direccionTrabajo: "",
      numeroCasaTrabajo: "",
      referenciaTrabajo: "",
      fechaIngresoInstitucion: "",
      antiguedadLaboral: "",
      ingresoAproxRango: "",
      ingresoAproxMonto: 0,
      giroEntidad: giroEntidadDefault,
      cargoDesempenado: "",
      areaTrabajo: "",
      telefonoTrabajo: "",
      tipoVivienda: "",
      poseeVehiculo: "No",
      numeroVehiculos: 0,
      poseeInmuebles: "No",
      descOtrosInmuebles: "",
      otrosIngresos: "No",
      descOtrosIngresos: "",
      montoOtrosIngresos: 0,
      dependientesEconomicos: 0,
      esPep: false,
      enviarRevision: false,
      noDesignacionBeneficiarios: false,
      perfilLaft: "A",
    });
    setPhotoDataUrl("");
    setPhotoBlocked(false);
    setJustificacionFoto("");
    setPhotoError("");
    setNoCorreo(false);
    setReferencias([]);
    setBeneficiarios([]);
    setPadecimientosList([]);
    setOtrosCargos([]);
    setPpeFamiliares([]);
    setPpeEmpresas([]);
    setFichaAdjunta(null);
    setFichaAdjuntaPep(null);
    setFieldErrors({});
    setSearchError("");
    setSaveFichaError("");
    setSaveFichaSuccess("");
  };

  const getBeneficiariosRequirementError = () => {
    const faltantes = [];
    if (totalBeneficiariosPotenciales < 1) {
      faltantes.push("agregar al menos 1 beneficiario potencial");
    }
    if (!form.noDesignacionBeneficiarios && totalPorcentajeLegal !== 100) {
      faltantes.push("completar 100% en beneficiarios legales");
    }
    return faltantes.length ? `Debe ${faltantes.join(" y ")}.` : "";
  };

  const renderReviewStep = () => (
    <Paper sx={{ ...sectionPaper, pt: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1.5,
        }}
      >
        <Box />
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleGuardarFinal}
          disabled={
            !fichaAdjunta || (form.esPep && !fichaAdjuntaPep) || savingRegistro
          }
        >
          {savingRegistro ? "Guardando..." : "Guardar"}
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: form.esPep ? "1fr 1fr" : "1fr",
          },
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <SectionTitle>Ficha de Afiliación</SectionTitle>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handleOpenFichaPreview}
            >
              Imprimir
            </Button>
            <TextField
              size="small"
              value={fichaAdjunta?.name || ""}
              placeholder="Archivo de ficha firmada"
              InputProps={{ readOnly: true }}
              sx={{ minWidth: 260, flex: 1 }}
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
            >
              Adjuntar
              <input
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleAdjuntarFicha}
              />
            </Button>
          </Box>
        </Box>
        {form.esPep && (
          <Box>
            <SectionTitle>Ficha de Persona Políticamente Expuesta</SectionTitle>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handleOpenFichaPepPreview}
              >
                Imprimir
              </Button>
              <TextField
                size="small"
                value={fichaAdjuntaPep?.name || ""}
                placeholder="Archivo de ficha PEP firmada"
                InputProps={{ readOnly: true }}
                sx={{ minWidth: 260, flex: 1 }}
              />
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
              >
                Adjuntar
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleAdjuntarFichaPep}
                />
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {saveFichaError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {saveFichaError}
        </Alert>
      )}
      {saveFichaSuccess && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {saveFichaSuccess}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          gap: 1.2,
        }}
      >
        <Paper sx={{ ...sectionPaper, minHeight: 270 }}>
          <SectionTitle>Información Personal</SectionTitle>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <Box
                sx={{
                  width: 130,
                  height: 150,
                  border: "1px solid #c8c8c8",
                  borderRadius: 0.5,
                  backgroundColor: "#fafafa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {photoDataUrl ? (
                  <img
                    src={photoDataUrl}
                    alt="Foto de afiliado"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <AccountCircleIcon sx={{ fontSize: 80, color: "#c5c7cc" }} />
                )}
              </Box>
              <IconButton
                size="small"
                onClick={handleOpenSummaryPhoto}
                title="Tomar fotografía"
                sx={{
                  position: "absolute",
                  bottom: 4,
                  right: 4,
                  background: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  p: 0.5,
                  "&:hover": { background: "rgba(25,73,140,0.85)" },
                }}
              >
                <CameraAltIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
          <Typography>
            <b>Tipo de enrolamiento:</b> {form.tipoDocumento || "-"}
          </Typography>
          <Typography>
            <b>Número de documento:</b>{" "}
            {formatIdentificacionVisual(form.identificacion) || "-"}
          </Typography>
          <Typography>
            <b>Primer nombre:</b> {form.primerNombre || "-"}
          </Typography>
          <Typography>
            <b>Segundo nombre:</b> {form.segundoNombre || "-"}
          </Typography>
          <Typography>
            <b>Primer apellido:</b> {form.primerApellido || "-"}
          </Typography>
          <Typography>
            <b>Segundo apellido:</b> {form.segundoApellido || "-"}
          </Typography>
          <Typography>
            <b>Fecha de nacimiento:</b> {form.fechaNacimiento || "-"}
          </Typography>
          <Typography>
            <b>Sexo:</b>{" "}
            {form.genero === "M"
              ? "MASCULINO"
              : form.genero === "F"
                ? "FEMENINO"
                : "-"}
          </Typography>
          <Typography>
            <b>Estado civil:</b> {getEstadoCivilLabel(form.estadoCivil) || "-"}
          </Typography>
          <Typography>
            <b>Profesión:</b> {form.profesion || "-"}
          </Typography>
        </Paper>
        <Paper sx={{ ...sectionPaper, minHeight: 270 }}>
          <SectionTitle>Información Demográfica</SectionTitle>
          <Typography>
            <b>País de nacimiento:</b> {form.paisNacimiento || "-"}
          </Typography>
          <Typography>
            <b>Departamento:</b> {form.departamentoResidencia || "-"}
          </Typography>
          <Typography>
            <b>Municipio:</b> {form.municipioResidencia || "-"}
          </Typography>
          <Typography>
            <b>Dirección de residencia:</b> {form.direccionCompleta || "-"}
          </Typography>
          <Typography>
            <b>Lugar de nacimiento:</b> {form.lugarNacimiento || "-"}
          </Typography>
          <Typography>
            <b>Teléfono:</b>{" "}
            {formatTelefonoVisual(form.telefono || form.movil) || "-"}
          </Typography>
        </Paper>
        <Paper sx={{ ...sectionPaper, minHeight: 270 }}>
          <SectionTitle>Información Laboral</SectionTitle>
          <Typography>
            <b>Empleador o Patrono:</b> {form.patrono || "-"}
          </Typography>
          <Typography>
            <b>Departamento de trabajo:</b> {form.departamentoTrabajo || "-"}
          </Typography>
          <Typography>
            <b>Municipio de trabajo:</b> {form.municipioTrabajo || "-"}
          </Typography>
          <Typography>
            <b>Dirección de trabajo:</b> {form.direccionTrabajo || "-"}
          </Typography>
          <Typography>
            <b>Teléfono de trabajo:</b>{" "}
            {formatTelefonoVisual(form.telefonoTrabajo) || "-"}
          </Typography>
        </Paper>
      </Box>
    </Paper>
  );

  const renderSearchStep = () => (
    <Box sx={{ maxWidth: 420, mt: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
          gap: 1,
          alignItems: "end",
          mb: 1,
        }}
      >
        <TextField
          fullWidth
          size="small"
          label="No. Identificación"
          value={formatIdentificacionVisual(form.identificacion)}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              identificacion: stripIdentificacionVisual(event.target.value),
            }))
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleBuscar();
            }
          }}
        />
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={handleBuscar}
          disabled={searchingPersona}
          sx={{
            borderColor: brandColor,
            color: brandColor,
            fontWeight: 600,
            "&:hover": { borderColor: brandColor },
          }}
        >
          {searchingPersona ? "Buscando..." : "Buscar"}
        </Button>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={afiliadoInjupemp}
            onChange={(e) => setAfiliadoInjupemp(e.target.checked)}
          />
        }
        label="Esta persona es un afiliado del Injupemp"
      />
      {searchError && (
        <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
          {searchError}
        </Alert>
      )}
    </Box>
  );

  const renderPersonalStep = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" },
        gap: 2,
      }}
    >
      <Paper sx={sectionPaper}>
        <SectionTitle>Información Personal</SectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.2 }}>
          <TextField
            size="small"
            label="Tipo de documento"
            value={form.tipoDocumento}
            disabled
            fullWidth
          />
          <TextField
            size="small"
            label="Número de identificación"
            value={formatIdentificacionVisual(form.identificacion)}
            InputProps={{ readOnly: true }}
            fullWidth
          />
          <TextField
            size="small"
            required
            error={Boolean(fieldErrors.primerNombre)}
            helperText={fieldErrors.primerNombre || ""}
            label="Primer nombre"
            value={form.primerNombre}
            onChange={handleChange("primerNombre")}
            fullWidth
          />
          <TextField
            size="small"
            label="Segundo nombre"
            value={form.segundoNombre}
            onChange={handleChange("segundoNombre")}
            fullWidth
          />
          <TextField
            size="small"
            required
            error={Boolean(fieldErrors.primerApellido)}
            helperText={fieldErrors.primerApellido || ""}
            label="Primer apellido"
            value={form.primerApellido}
            onChange={handleChange("primerApellido")}
            fullWidth
          />
          <TextField
            size="small"
            label="Segundo apellido"
            value={form.segundoApellido}
            onChange={handleChange("segundoApellido")}
            fullWidth
          />
          <FormControl
            size="small"
            required
            error={Boolean(fieldErrors.genero)}
            fullWidth
          >
            <InputLabel>Género</InputLabel>
            <Select
              value={form.genero}
              label="Género"
              onChange={handleChange("genero")}
              disabled={loadingCatalogos}
            >
              {sexoOptions.map((option) => (
                <MenuItem key={`sexo-${option.value}`} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            required
            error={Boolean(fieldErrors.paisNacimiento)}
            fullWidth
          >
            <InputLabel>País de nacimiento</InputLabel>
            <Select
              value={form.paisNacimiento}
              label="País de nacimiento"
              onChange={handleChange("paisNacimiento")}
              disabled={loadingCatalogos}
            >
              {paisNacimientoOptions.map((option) => (
                <MenuItem key={`pais-nacimiento-${option}`} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            required
            error={Boolean(fieldErrors.nacionalidad)}
            fullWidth
          >
            <InputLabel>Nacionalidad</InputLabel>
            <Select
              value={form.nacionalidad}
              label="Nacionalidad"
              onChange={handleChange("nacionalidad")}
              disabled={loadingCatalogos}
            >
              {nacionalidadOptions.map((option) => (
                <MenuItem key={`nacionalidad-${option}`} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            required
            error={Boolean(fieldErrors.fechaNacimiento)}
            helperText={fieldErrors.fechaNacimiento || ""}
            type="date"
            label="Fecha de nacimiento"
            value={form.fechaNacimiento}
            onChange={handleChange("fechaNacimiento")}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            size="small"
            required
            error={Boolean(fieldErrors.lugarNacimiento)}
            helperText={fieldErrors.lugarNacimiento || ""}
            label="Lugar de nacimiento"
            value={form.lugarNacimiento}
            onChange={handleChange("lugarNacimiento")}
            fullWidth
          />
          <FormControl
            size="small"
            required
            error={Boolean(fieldErrors.estadoCivil)}
            fullWidth
          >
            <InputLabel>Estado civil</InputLabel>
            <Select
              value={form.estadoCivil}
              label="Estado civil"
              onChange={handleChange("estadoCivil")}
              disabled={loadingCatalogos}
            >
              {estadoCivilOptions.map((option) => (
                <MenuItem
                  key={`estado-civil-${option.value}`}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper sx={sectionPaper}>
        <SectionTitle>Información de Residencia</SectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.2 }}>
          <Autocomplete
            options={departamentoResidenciaOptions}
            value={form.departamentoResidencia || null}
            onChange={(_event, value) =>
              handleDepartamentoResidenciaSelect(asText(value))
            }
            onInputChange={(_event, _value, reason) => {
              if (reason === "clear") handleDepartamentoResidenciaSelect("");
            }}
            disabled={loadingCatalogos}
            noOptionsText="Sin coincidencias"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                required
                error={Boolean(fieldErrors.departamentoResidencia)}
                helperText={fieldErrors.departamentoResidencia || ""}
                label="Departamento de Residencia"
              />
            )}
          />
          <Autocomplete
            options={municipioResidenciaOptions}
            value={form.municipioResidencia || null}
            onChange={(_event, value) =>
              handleMunicipioResidenciaSelect(asText(value))
            }
            onInputChange={(_event, _value, reason) => {
              if (reason === "clear") handleMunicipioResidenciaSelect("");
            }}
            disabled={loadingCatalogos || !form.departamentoResidencia}
            noOptionsText="Sin coincidencias"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                required
                error={Boolean(fieldErrors.municipioResidencia)}
                helperText={fieldErrors.municipioResidencia || ""}
                label="Municipio de Residencia"
              />
            )}
          />
          {coloniaResidenciaOptions.length > 0 ? (
            <Autocomplete
              options={coloniaResidenciaOptions}
              value={form.coloniaResidencia || null}
              onChange={(_event, value) => handleColoniaResidenciaSelect(value)}
              onInputChange={(_event, _value, reason) => {
                if (reason === "clear") handleColoniaResidenciaSelect("");
              }}
              disabled={loadingCatalogos || !form.municipioResidencia}
              noOptionsText="Sin coincidencias"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  required
                  error={Boolean(fieldErrors.coloniaResidencia)}
                  helperText={fieldErrors.coloniaResidencia || ""}
                  label="Colonia de residencia"
                />
              )}
            />
          ) : (
            <TextField
              size="small"
              required
              error={Boolean(fieldErrors.coloniaResidencia)}
              helperText={fieldErrors.coloniaResidencia || ""}
              label="Colonia de residencia"
              value={form.coloniaResidencia}
              onChange={handleChange("coloniaResidencia")}
            />
          )}
          <TextField
            size="small"
            required
            error={Boolean(fieldErrors.direccionCompleta)}
            helperText={fieldErrors.direccionCompleta || ""}
            label="Dirección completa"
            multiline
            rows={2}
            value={form.direccionCompleta}
            onChange={handleChange("direccionCompleta")}
          />
          <TextField
            size="small"
            label="Calle"
            value={form.calle}
            onChange={handleChange("calle")}
          />
          <TextField
            size="small"
            label="Número de casa"
            value={form.numeroCasa}
            onChange={handleChange("numeroCasa")}
          />
          <TextField
            size="small"
            label="Referencia de dirección"
            multiline
            rows={2}
            value={form.referenciaDireccion}
            onChange={handleChange("referenciaDireccion")}
          />
          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}
          >
            <TextField
              size="small"
              type="number"
              label="Tiempo de residir (años)"
              value={form.tiempoResidir}
              inputProps={{ min: 0, max: MAX_TIEMPO_RESIDIR }}
              onChange={handleTiempoResidirChange}
            />
            <TextField
              size="small"
              required
              error={Boolean(fieldErrors.fechaDesdeReside)}
              helperText={fieldErrors.fechaDesdeReside || ""}
              type="date"
              label="Fecha desde cuando reside"
              value={form.fechaDesdeReside}
              onChange={handleFechaDesdeResideChange}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}
          >
            <TextField
              size="small"
              label="Teléfono"
              value={formatTelefonoVisual(form.telefono)}
              onChange={handleChange("telefono")}
            />
            <TextField
              size="small"
              required
              error={Boolean(fieldErrors.movil)}
              helperText={fieldErrors.movil || ""}
              label="Móvil"
              value={formatTelefonoVisual(form.movil)}
              onChange={handleChange("movil")}
            />
          </Box>
        </Box>
      </Paper>

      <Paper sx={sectionPaper}>
        <SectionTitle>Otros datos</SectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.2 }}>
          <TextField
            size="small"
            required={!noCorreo}
            error={Boolean(fieldErrors.correo)}
            helperText={fieldErrors.correo || ""}
            label="Correo electrónico"
            value={form.correo}
            onChange={handleChange("correo")}
            onBlur={handleCorreoBlur}
            disabled={noCorreo}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={noCorreo}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setNoCorreo(checked);
                  if (checked) {
                    setFieldErrors((prev) => {
                      if (!prev.correo) return prev;
                      const next = { ...prev };
                      delete next.correo;
                      return next;
                    });
                  }
                }}
              />
            }
            label="No cuenta con correo electrónico"
          />
          {tipoClienteOptions.length > 0 ? (
            <FormControl
              size="small"
              required
              error={Boolean(fieldErrors.tipoCliente)}
            >
              <InputLabel>Tipo de cliente</InputLabel>
              <Select
                label="Tipo de cliente"
                value={form.tipoCliente}
                onChange={handleChange("tipoCliente")}
                disabled={loadingCatalogos}
              >
                {tipoClienteOptions.map((option) => (
                  <MenuItem key={`tipo-cliente-${option}`} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.tipoCliente && (
                <Typography variant="caption" sx={{ color: "#d32f2f" }}>
                  {fieldErrors.tipoCliente}
                </Typography>
              )}
            </FormControl>
          ) : (
            <TextField
              size="small"
              required
              error={Boolean(fieldErrors.tipoCliente)}
              helperText={fieldErrors.tipoCliente || ""}
              label="Tipo de cliente"
              value={form.tipoCliente}
              onChange={handleChange("tipoCliente")}
            />
          )}
          <TextField
            size="small"
            label="RTN"
            value={form.rtn}
            onChange={handleChange("rtn")}
          />
          {profesionSearchOptions.length > 0 ? (
            <Autocomplete
              options={profesionSearchOptions}
              value={form.profesion || null}
              onChange={(_event, value) => handleProfesionSelect(value)}
              onInputChange={(_event, _value, reason) => {
                if (reason === "clear") handleProfesionSelect("");
              }}
              disabled={loadingCatalogos}
              noOptionsText="Sin coincidencias"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  required
                  error={Boolean(fieldErrors.profesion)}
                  helperText={fieldErrors.profesion || ""}
                  label="Profesión y oficio"
                />
              )}
            />
          ) : (
            <TextField
              size="small"
              required
              error={Boolean(fieldErrors.profesion)}
              helperText={fieldErrors.profesion || ""}
              label="Profesión y oficio"
              value={form.profesion}
              onChange={handleChange("profesion")}
            />
          )}
          {actividadEconomicaCnbsSearchOptions.length > 0 ? (
            <Autocomplete
              options={actividadEconomicaCnbsSearchOptions}
              filterOptions={filterActividadEconomicaCnbsOptions}
              getOptionLabel={(option) => normalizeText(option)}
              value={form.actividadEconomicaCnbs || null}
              onChange={(_event, value) =>
                handleActividadEconomicaCnbsSelect(asText(value))
              }
              onInputChange={(_event, _value, reason) => {
                if (reason === "clear") handleActividadEconomicaCnbsSelect("");
              }}
              disabled={loadingCatalogos}
              noOptionsText="Sin coincidencias"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  required
                  error={Boolean(fieldErrors.actividadEconomicaCnbs)}
                  helperText={fieldErrors.actividadEconomicaCnbs || ""}
                  label="Actividad económica CNBS"
                />
              )}
            />
          ) : (
            <TextField
              size="small"
              required
              error={Boolean(fieldErrors.actividadEconomicaCnbs)}
              helperText={fieldErrors.actividadEconomicaCnbs || ""}
              label="Actividad económica CNBS"
              value={form.actividadEconomicaCnbs}
              onChange={handleChange("actividadEconomicaCnbs")}
            />
          )}
          {profesionCnbsSearchOptions.length > 0 ? (
            <Autocomplete
              options={profesionCnbsSearchOptions}
              getOptionLabel={(option) => normalizeText(option)}
              value={form.profesionCnbs || null}
              onChange={(_event, value) =>
                handleProfesionCnbsSelect(asText(value))
              }
              onInputChange={(_event, _value, reason) => {
                if (reason === "clear") handleProfesionCnbsSelect("");
              }}
              disabled={loadingCatalogos}
              noOptionsText="Sin coincidencias"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  required
                  error={Boolean(fieldErrors.profesionCnbs)}
                  helperText={fieldErrors.profesionCnbs || ""}
                  label="Profesión CNBS"
                />
              )}
            />
          ) : (
            <TextField
              size="small"
              required
              error={Boolean(fieldErrors.profesionCnbs)}
              helperText={fieldErrors.profesionCnbs || ""}
              label="Profesión CNBS"
              value={form.profesionCnbs}
              onChange={handleChange("profesionCnbs")}
            />
          )}
          {nivelEducativoOptions.length > 0 ? (
            <FormControl
              size="small"
              required
              error={Boolean(fieldErrors.nivelEducativo)}
            >
              <InputLabel>Nivel educativo</InputLabel>
              <Select
                label="Nivel educativo"
                value={form.nivelEducativo}
                onChange={handleChange("nivelEducativo")}
                disabled={loadingCatalogos}
              >
                {nivelEducativoOptions.map((option) => (
                  <MenuItem key={`nivel-edu-${option}`} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.nivelEducativo && (
                <Typography variant="caption" sx={{ color: "#d32f2f" }}>
                  {fieldErrors.nivelEducativo}
                </Typography>
              )}
            </FormControl>
          ) : (
            <TextField
              size="small"
              required
              error={Boolean(fieldErrors.nivelEducativo)}
              helperText={fieldErrors.nivelEducativo || ""}
              label="Nivel educativo"
              value={form.nivelEducativo}
              onChange={handleChange("nivelEducativo")}
            />
          )}
          <TextField
            size="small"
            label="Nombre del conyuge"
            value={form.nombreConyuge}
            onChange={handleChange("nombreConyuge")}
          />
          {documentoTipoConyugeOptions.length > 0 ? (
            <TextField
              size="small"
              select
              label="Tipo de documento (conyuge)"
              value={form.tipoDocumentoConyuge}
              onChange={handleChange("tipoDocumentoConyuge")}
              disabled={loadingCatalogos}
            >
              {documentoTipoConyugeOptions.map((option) => (
                <MenuItem key={`doc-conyuge-${option}`} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              size="small"
              label="Tipo de documento (conyuge)"
              value={form.tipoDocumentoConyuge}
              onChange={handleChange("tipoDocumentoConyuge")}
            />
          )}
          <TextField
            size="small"
            label="Número de identificación (conyuge)"
            value={formatIdentificacionVisual(form.identificacionConyuge)}
            onChange={handleChange("identificacionConyuge")}
          />
          {sexoConyugeOptions.length > 0 ? (
            <TextField
              size="small"
              select
              label="Género (conyuge)"
              value={form.generoConyuge}
              onChange={handleChange("generoConyuge")}
              disabled={loadingCatalogos}
            >
              {sexoConyugeOptions.map((option) => (
                <MenuItem key={`sexo-conyuge-${option}`} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              size="small"
              label="Género (conyuge)"
              value={form.generoConyuge}
              onChange={handleChange("generoConyuge")}
            />
          )}
          <TextField
            size="small"
            type="date"
            label="Fecha de nacimiento (conyuge)"
            value={form.fechaNacimientoConyuge}
            onChange={handleChange("fechaNacimientoConyuge")}
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            control={
              <Checkbox checked={form.esPep} onChange={handleChange("esPep")} />
            }
            label="Es una persona expuesta políticamente"
          />
        </Box>
      </Paper>
    </Box>
  );

  const renderPhotoStep = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 3,
      }}
    >
      <Box
        sx={{
          width: 280,
          height: 360,
          border: "1px solid #bdbdbd",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f6f6f6",
          overflow: "hidden",
        }}
      >
        {photoDataUrl ? (
          <img
            src={photoDataUrl}
            alt="Foto capturada"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : cameraOpen ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <AccountCircleIcon sx={{ fontSize: 180, color: "#c5c7cc" }} />
        )}
      </Box>
      <Button
        variant="outlined"
        startIcon={<PlayArrowIcon />}
        sx={{ mt: 1.2 }}
        onClick={handleTomarFoto}
        disabled={photoBlocked}
      >
        {cameraOpen ? "Capturar Foto" : "Tomar Foto"}
      </Button>
      {photoDataUrl && !photoBlocked && (
        <Button
          variant="text"
          size="small"
          sx={{ mt: 0.5 }}
          onClick={() => {
            setPhotoDataUrl("");
            setPhotoError("");
            openCamera();
          }}
        >
          Volver a tomar foto
        </Button>
      )}
      <FormControlLabel
        sx={{ mt: 1, display: "none" }}
        control={
          <Checkbox
            checked={photoBlocked}
            onChange={(event) => {
              const checked = event.target.checked;
              setPhotoBlocked(checked);
              setPhotoError("");
              setCameraError("");
              if (checked) {
                setPhotoDataUrl("");
                stopCamera();
              }
            }}
          />
        }
        label="No se le puede capturar la foto a la persona"
      />
      {photoBlocked && (
        <TextField
          label="Justificación"
          multiline
          minRows={3}
          sx={{ mt: 1, width: 420, maxWidth: "100%" }}
          value={justificacionFoto}
          onChange={(event) => {
            setJustificacionFoto(event.target.value);
            if (photoError) setPhotoError("");
          }}
          error={Boolean(photoError && isBlank(justificacionFoto))}
          helperText={
            photoError && isBlank(justificacionFoto)
              ? photoError
              : "Campo obligatorio cuando no se pueda capturar la foto."
          }
        />
      )}
      {cameraError && (
        <Alert severity="warning" sx={{ mt: 1, width: 420, maxWidth: "100%" }}>
          {cameraError}
        </Alert>
      )}
      {photoError && !photoBlocked && (
        <Alert severity="warning" sx={{ mt: 1, width: 420, maxWidth: "100%" }}>
          {photoError}
        </Alert>
      )}
    </Box>
  );

  const renderTabInformacionLaboral = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        gap: 2,
        pt: 1,
      }}
    >
      <Paper sx={sectionPaper}>
        <SectionTitle>Información Laboral</SectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.2 }}>
          <TextField
            size="small"
            required
            error={Boolean(fieldErrors.patrono)}
            helperText={fieldErrors.patrono || ""}
            label="Nombre del Empleador / Patrono"
            value={form.patrono}
            onChange={handleChange("patrono")}
          />
          <Autocomplete
            options={departamentoTrabajoOptions}
            value={form.departamentoTrabajo || null}
            onChange={(_event, value) =>
              handleDepartamentoTrabajoSelect(asText(value))
            }
            onInputChange={(_event, _value, reason) => {
              if (reason === "clear") handleDepartamentoTrabajoSelect("");
            }}
            disabled={loadingCatalogos}
            noOptionsText="Sin coincidencias"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                required
                error={Boolean(fieldErrors.departamentoTrabajo)}
                helperText={fieldErrors.departamentoTrabajo || ""}
                label="Departamento de trabajo"
              />
            )}
          />
          <Autocomplete
            options={municipioTrabajoOptions}
            value={form.municipioTrabajo || null}
            onChange={(_event, value) =>
              handleMunicipioTrabajoSelect(asText(value))
            }
            onInputChange={(_event, _value, reason) => {
              if (reason === "clear") handleMunicipioTrabajoSelect("");
            }}
            disabled={loadingCatalogos || !form.departamentoTrabajo}
            noOptionsText="Sin coincidencias"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                required
                error={Boolean(fieldErrors.municipioTrabajo)}
                helperText={fieldErrors.municipioTrabajo || ""}
                label="Municipio de trabajo"
              />
            )}
          />
          <Autocomplete
            options={coloniaTrabajoOptions}
            value={form.coloniaTrabajo || null}
            onChange={(_event, value) =>
              handleColoniaTrabajoSelect(asText(value))
            }
            onInputChange={(_event, _value, reason) => {
              if (reason === "clear") handleColoniaTrabajoSelect("");
            }}
            disabled={loadingCatalogos || !form.municipioTrabajo}
            noOptionsText="Sin coincidencias"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                required
                error={Boolean(fieldErrors.coloniaTrabajo)}
                helperText={fieldErrors.coloniaTrabajo || ""}
                label="Colonia de trabajo"
              />
            )}
          />
          <TextField
            size="small"
            label="Dirección de trabajo"
            multiline
            rows={2}
            value={form.direccionTrabajo}
            onChange={handleChange("direccionTrabajo")}
          />
          <TextField
            size="small"
            label="Número de casa"
            value={form.numeroCasaTrabajo}
            onChange={handleChange("numeroCasaTrabajo")}
          />
          <TextField
            size="small"
            label="Referencia de ubicación"
            multiline
            rows={2}
            value={form.referenciaTrabajo}
            onChange={handleChange("referenciaTrabajo")}
          />
        </Box>
      </Paper>
      <Paper sx={sectionPaper}>
        <SectionTitle>Información Adicional</SectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.2 }}>
          <TextField
            size="small"
            required
            error={Boolean(fieldErrors.fechaIngresoInstitucion)}
            helperText={fieldErrors.fechaIngresoInstitucion || ""}
            type="date"
            label="Fecha de ingreso a la institución"
            value={form.fechaIngresoInstitucion}
            onChange={handleFechaIngresoInstitucionChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            required
            error={Boolean(fieldErrors.antiguedadLaboral)}
            helperText={fieldErrors.antiguedadLaboral || ""}
            label="Antigüedad laboral"
            value={form.antiguedadLaboral}
            InputProps={{ readOnly: true }}
          />
          <Autocomplete
            options={ingresoAproxRangoOptions}
            value={form.ingresoAproxRango || null}
            onChange={(_event, value) =>
              handleIngresoAproxRangoSelect(asText(value))
            }
            onInputChange={(_event, _value, reason) => {
              if (reason === "clear") handleIngresoAproxRangoSelect("");
            }}
            disabled={loadingCatalogos}
            noOptionsText="Sin coincidencias"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                required
                error={Boolean(fieldErrors.ingresoAproxRango)}
                helperText={fieldErrors.ingresoAproxRango || ""}
                label="Ingresos aproximados (rango)"
              />
            )}
          />
          <TextField
            size="small"
            required
            error={Boolean(fieldErrors.ingresoAproxMonto)}
            helperText={fieldErrors.ingresoAproxMonto || ""}
            type="number"
            label="Ingresos aproximados (monto)"
            value={form.ingresoAproxMonto}
            onChange={handleChange("ingresoAproxMonto")}
          />
          <Autocomplete
            options={giroEntidadOptions}
            value={form.giroEntidad || null}
            onChange={(_event, value) => handleGiroEntidadSelect(asText(value))}
            onInputChange={(_event, _value, reason) => {
              if (reason === "clear") handleGiroEntidadSelect("");
            }}
            disabled={loadingCatalogos}
            noOptionsText="Sin coincidencias"
            renderInput={(params) => (
              <TextField {...params} size="small" label="Giro de la entidad" />
            )}
          />
          <Autocomplete
            options={cargoDesempenadoOptions}
            filterOptions={filterTextOptions}
            value={form.cargoDesempenado || null}
            onChange={(_event, value) =>
              handleCargoDesempenadoSelect(asText(value))
            }
            onInputChange={(_event, _value, reason) => {
              if (reason === "clear") handleCargoDesempenadoSelect("");
            }}
            disabled={loadingCatalogos}
            noOptionsText="Sin coincidencias"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                required
                error={Boolean(fieldErrors.cargoDesempenado)}
                helperText={fieldErrors.cargoDesempenado || ""}
                label="Cargo desempeñado"
              />
            )}
          />
          <TextField
            size="small"
            label="Área de trabajo"
            value={form.areaTrabajo}
            onChange={handleChange("areaTrabajo")}
          />
          <TextField
            size="small"
            required
            error={Boolean(fieldErrors.telefonoTrabajo)}
            helperText={fieldErrors.telefonoTrabajo || ""}
            label="Teléfono de trabajo"
            value={formatTelefonoVisual(form.telefonoTrabajo)}
            onChange={handleChange("telefonoTrabajo")}
          />
        </Box>
      </Paper>
      <Paper
        sx={{ ...sectionPaper, gridColumn: { xs: "span 1", lg: "span 2" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <SectionTitle>
            Otros cargos públicos desempeñados en los últimos cuatro años
          </SectionTitle>
          <Button
            startIcon={<AddCircleIcon />}
            variant="outlined"
            onClick={() => handleOpenCargoModal()}
          >
            Agregar
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeaderStyle}>No</TableCell>
                <TableCell sx={tableHeaderStyle}>Empleador / Patrono</TableCell>
                <TableCell sx={tableHeaderStyle}>Cargo Desempeñado</TableCell>
                <TableCell sx={tableHeaderStyle}>
                  Actividad o Giro de la Empresa
                </TableCell>
                <TableCell sx={tableHeaderStyle} />
              </TableRow>
            </TableHead>
            <TableBody>
              {otrosCargos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: "#7d7d7d" }}>
                    Sin registros.
                  </TableCell>
                </TableRow>
              ) : (
                otrosCargos.map((cargo, index) => (
                  <TableRow key={cargo.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{cargo.empleadorPatrono}</TableCell>
                    <TableCell>{cargo.cargoDesempenado}</TableCell>
                    <TableCell>{cargo.actividadGiro}</TableCell>
                    <TableCell width={72} sx={{ whiteSpace: "nowrap" }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenCargoModal(cargo)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setOtrosCargos((prev) =>
                            prev.filter((c) => c.id !== cargo.id),
                          )
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Dialog
        open={openCargoModal}
        onClose={handleCloseCargoModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingCargoId !== null
            ? "Editar Cargo"
            : "Cargo Público Desempeñado"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1.3,
              mt: 0.5,
            }}
          >
            <TextField
              size="small"
              label="Empleador/Patrono"
              value={cargoForm.empleadorPatrono}
              onChange={handleCargoFormChange("empleadorPatrono")}
              required
              error={Boolean(cargoModalError.empleadorPatrono)}
              helperText={cargoModalError.empleadorPatrono || ""}
            />
            <Autocomplete
              options={cargoCatalogOptions}
              filterOptions={filterTextOptions}
              value={cargoForm.cargoDesempenado || null}
              onChange={(_event, value) =>
                handleCargoModalCatalogSelect("cargoDesempenado", asText(value))
              }
              onInputChange={(_event, _value, reason) => {
                if (reason === "clear")
                  handleCargoModalCatalogSelect("cargoDesempenado", "");
              }}
              disabled={loadingCatalogos}
              noOptionsText="Sin coincidencias"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Cargo Desempeñado"
                  required
                  error={Boolean(cargoModalError.cargoDesempenado)}
                  helperText={cargoModalError.cargoDesempenado || ""}
                />
              )}
            />
            <Autocomplete
              options={giroCatalogOptions}
              value={cargoForm.actividadGiro || null}
              onChange={(_event, value) =>
                handleCargoModalCatalogSelect("actividadGiro", asText(value))
              }
              onInputChange={(_event, _value, reason) => {
                if (reason === "clear")
                  handleCargoModalCatalogSelect("actividadGiro", "");
              }}
              disabled={loadingCatalogos}
              noOptionsText="Sin coincidencias"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Actividad o giro de la empresa"
                  required
                  error={Boolean(cargoModalError.actividadGiro)}
                  helperText={cargoModalError.actividadGiro || ""}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCargoModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAgregarCargo}
            sx={{ color: "#fff" }}
          >
            {editingCargoId !== null ? "Guardar" : "Agregar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const renderTabPatrimonio = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        gap: 2,
        pt: 1,
      }}
    >
      <Paper sx={sectionPaper}>
        <SectionTitle>Patrimonio</SectionTitle>
        <Autocomplete
          options={tipoViviendaOptions}
          value={form.tipoVivienda || null}
          onChange={(_event, value) => handleTipoViviendaSelect(asText(value))}
          onInputChange={(_event, _value, reason) => {
            if (reason === "clear") handleTipoViviendaSelect("");
          }}
          disabled={loadingCatalogos}
          noOptionsText="Sin coincidencias"
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              required
              error={Boolean(fieldErrors.tipoVivienda)}
              helperText={fieldErrors.tipoVivienda || ""}
              fullWidth
              label="Tipo de vivienda que posee"
            />
          )}
          sx={{ mb: 1.5 }}
        />
        <FormControl
          required
          error={Boolean(fieldErrors.poseeVehiculo)}
          sx={{ mb: 1.5 }}
        >
          <FormLabel>¿Posee vehículo?</FormLabel>
          <RadioGroup
            row
            value={form.poseeVehiculo}
            onChange={(event) => {
              const value = event.target.value;
              setForm((prev) => ({ ...prev, poseeVehiculo: value }));
              if (value === "No") {
                setFieldErrors((prev) => {
                  const next = { ...prev };
                  delete next.numeroVehiculos;
                  return next;
                });
              }
            }}
          >
            {genericYesNo.map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio size="small" />}
                label={value}
              />
            ))}
          </RadioGroup>
          {fieldErrors.poseeVehiculo && (
            <Typography variant="caption" sx={{ color: "#d32f2f" }}>
              {fieldErrors.poseeVehiculo}
            </Typography>
          )}
        </FormControl>
        {form.poseeVehiculo === "Si" && (
          <TextField
            size="small"
            type="number"
            fullWidth
            required
            error={Boolean(fieldErrors.numeroVehiculos)}
            helperText={fieldErrors.numeroVehiculos || ""}
            label="Número de vehículos"
            value={form.numeroVehiculos}
            onChange={handleChange("numeroVehiculos")}
            inputProps={{ min: 1 }}
            sx={{ mb: 1.5 }}
          />
        )}
        <FormControl required error={Boolean(fieldErrors.poseeInmuebles)}>
          <FormLabel>¿Posee otros inmuebles?</FormLabel>
          <RadioGroup
            row
            value={form.poseeInmuebles}
            onChange={(event) => {
              const value = event.target.value;
              setForm((prev) => ({ ...prev, poseeInmuebles: value }));
              if (value === "No") {
                setFieldErrors((prev) => {
                  const next = { ...prev };
                  delete next.descOtrosInmuebles;
                  return next;
                });
              }
            }}
          >
            {genericYesNo.map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio size="small" />}
                label={value}
              />
            ))}
          </RadioGroup>
          {fieldErrors.poseeInmuebles && (
            <Typography variant="caption" sx={{ color: "#d32f2f" }}>
              {fieldErrors.poseeInmuebles}
            </Typography>
          )}
        </FormControl>
        {form.poseeInmuebles === "Si" && (
          <TextField
            size="small"
            fullWidth
            required
            error={Boolean(fieldErrors.descOtrosInmuebles)}
            helperText={fieldErrors.descOtrosInmuebles || ""}
            label="Describa los otros inmuebles"
            value={form.descOtrosInmuebles}
            onChange={handleChange("descOtrosInmuebles")}
          />
        )}
      </Paper>

      <Paper sx={sectionPaper}>
        <SectionTitle>Otros ingresos</SectionTitle>
        <FormControl
          required
          error={Boolean(fieldErrors.otrosIngresos)}
          sx={{ mb: 1.5 }}
        >
          <FormLabel>¿Cuenta con otro tipo de ingreso?</FormLabel>
          <RadioGroup
            row
            value={form.otrosIngresos}
            onChange={(event) => {
              const value = event.target.value;
              setForm((prev) => ({ ...prev, otrosIngresos: value }));
              if (value === "No") {
                setFieldErrors((prev) => {
                  const next = { ...prev };
                  delete next.descOtrosIngresos;
                  delete next.montoOtrosIngresos;
                  return next;
                });
              }
            }}
          >
            {genericYesNo.map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio size="small" />}
                label={value}
              />
            ))}
          </RadioGroup>
          {fieldErrors.otrosIngresos && (
            <Typography variant="caption" sx={{ color: "#d32f2f" }}>
              {fieldErrors.otrosIngresos}
            </Typography>
          )}
        </FormControl>
        {form.otrosIngresos === "Si" && (
          <>
            <TextField
              size="small"
              required
              error={Boolean(fieldErrors.descOtrosIngresos)}
              helperText={fieldErrors.descOtrosIngresos || ""}
              fullWidth
              label="Describa el origen de otros ingresos"
              value={form.descOtrosIngresos}
              onChange={handleChange("descOtrosIngresos")}
              sx={{ mb: 1.5 }}
            />
            <TextField
              size="small"
              required
              error={Boolean(fieldErrors.montoOtrosIngresos)}
              helperText={fieldErrors.montoOtrosIngresos || ""}
              type="number"
              fullWidth
              label="Monto aproximado de otros ingresos"
              value={form.montoOtrosIngresos}
              onChange={handleChange("montoOtrosIngresos")}
              sx={{ mb: 1.5 }}
            />
          </>
        )}
        <TextField
          size="small"
          required
          error={Boolean(fieldErrors.dependientesEconomicos)}
          helperText={fieldErrors.dependientesEconomicos || ""}
          type="number"
          fullWidth
          label="Número de dependientes económicos"
          value={form.dependientesEconomicos}
          onChange={handleChange("dependientesEconomicos")}
        />
      </Paper>
    </Box>
  );

  const renderTabReferencias = () => (
    <Paper sx={{ ...sectionPaper, pt: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <SectionTitle>
          Listado de referencias laborales, personales y bancarias
        </SectionTitle>
        <Button
          startIcon={<AddCircleIcon />}
          variant="outlined"
          onClick={() => handleOpenReferenciaModal()}
        >
          Agregar
        </Button>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderStyle}>Tipo de Referencia</TableCell>
              <TableCell sx={tableHeaderStyle}>Nombre Completo</TableCell>
              <TableCell sx={tableHeaderStyle}>Dirección</TableCell>
              <TableCell sx={tableHeaderStyle}>Teléfono</TableCell>
              <TableCell sx={tableHeaderStyle} />
            </TableRow>
          </TableHead>
          <TableBody>
            {referencias.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ color: "#7d7d7d" }}>
                  Sin registros.
                </TableCell>
              </TableRow>
            ) : (
              referencias.map((referencia) => (
                <TableRow key={referencia.id}>
                  <TableCell>{referencia.tipoReferencia}</TableCell>
                  <TableCell>{referencia.nombreCompleto}</TableCell>
                  <TableCell>{referencia.direccion}</TableCell>
                  <TableCell>
                    {formatTelefonoVisual(referencia.telefono)}
                  </TableCell>
                  <TableCell width={80} sx={{ whiteSpace: "nowrap" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenReferenciaModal(referencia)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setReferencias((prev) =>
                          prev.filter((r) => r.id !== referencia.id),
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography sx={{ mt: 1, color: "#4d4d4d", fontSize: 13 }}>
        {getReferenciasRequirementSummary()}
      </Typography>
      <Typography sx={{ mt: 0.5, color: "#4d4d4d", fontSize: 13 }}>
        Cargadas: {getReferenceCountByType("COMERCIALES / BANCARIAS")}{" "}
        Comercial/Bancaria, {getReferenceCountByType("LABORALES")} Laborales,{" "}
        {getReferenceCountByType("PERSONALES")} Personales.
      </Typography>
      {referenciaRequirementError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {referenciaRequirementError}
        </Alert>
      )}
      <Dialog
        open={openReferenciaModal}
        onClose={handleCloseReferenciaModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingReferenciaId !== null
            ? "Editar Referencia"
            : "Agregar Referencia"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1.3,
              mt: 0.5,
            }}
          >
            <FormControl
              size="small"
              required
              error={Boolean(referenciaModalError.tipoReferencia)}
            >
              <InputLabel>Tipo de Referencia</InputLabel>
              <Select
                label="Tipo de Referencia"
                value={referenciaForm.tipoReferencia}
                onChange={handleReferenciaFormChange("tipoReferencia")}
              >
                {referenciaTipoOptions.map((tipo) => (
                  <MenuItem key={tipo} value={tipo}>
                    {tipo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Nombre Completo"
              value={referenciaForm.nombreCompleto}
              onChange={handleReferenciaFormChange("nombreCompleto")}
              required
              error={Boolean(referenciaModalError.nombreCompleto)}
              helperText={referenciaModalError.nombreCompleto || ""}
            />
            <TextField
              size="small"
              label="Teléfono"
              value={formatTelefonoVisual(referenciaForm.telefono)}
              onChange={handleReferenciaFormChange("telefono")}
              required
              error={Boolean(referenciaModalError.telefono)}
              helperText={referenciaModalError.telefono || ""}
            />
            {referenciaForm.tipoReferencia !== "COMERCIALES / BANCARIAS" && (
              <TextField
                size="small"
                label="Dirección"
                value={referenciaForm.direccion}
                onChange={handleReferenciaFormChange("direccion")}
                multiline
                minRows={2}
                required
                error={Boolean(referenciaModalError.direccion)}
                helperText={referenciaModalError.direccion || ""}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseReferenciaModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAgregarReferencia}
            sx={{ color: "#fff" }}
          >
            {editingReferenciaId !== null ? "Guardar" : "Agregar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );

  const renderTabPadecimientos = () => (
    <Paper sx={{ ...sectionPaper, pt: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <SectionTitle>Listado de Padecimientos Médicos</SectionTitle>
        <Button
          startIcon={<AddCircleIcon />}
          variant="outlined"
          onClick={handleOpenPadecimientoModal}
        >
          Agregar
        </Button>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderStyle}>Padecimiento</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {padecimientosList.length === 0 ? (
              <TableRow>
                <TableCell sx={{ color: "#7d7d7d" }}>Sin registros.</TableCell>
              </TableRow>
            ) : (
              padecimientosList.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.nombre}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={openPadecimientoModal}
        onClose={handleClosePadecimientoModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Padecimiento Médico</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <FormControl
            size="small"
            fullWidth
            required
            error={Boolean(padecimientoModalError)}
            sx={{ mt: 1 }}
          >
            <InputLabel>Seleccione un padecimiento médico</InputLabel>
            <Select
              label="Seleccione un padecimiento médico"
              value={padecimientoForm.nombre}
              onChange={(event) => {
                setPadecimientoForm({ nombre: event.target.value });
                setPadecimientoModalError("");
              }}
            >
              {padecimientoOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {padecimientoModalError && (
              <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5 }}>
                {padecimientoModalError}
              </Typography>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePadecimientoModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAgregarPadecimiento}
            sx={{ color: "#fff" }}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );

  const renderTabPpe = () => (
    <Paper sx={{ ...sectionPaper, pt: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography sx={{ color: "#3a3a3a", fontSize: 14 }}>
          Favor indicar nombre de familiares hasta un segundo grado de
          consanguinidad y afinidad: Conyuge(1). Hijos(2). Padres(3).
          Hermanos(4). Nietos(5). Nuera/Yerno(6). Abuelos(7)
        </Typography>
        <Button
          startIcon={<AddCircleIcon />}
          variant="outlined"
          onClick={() => handleOpenPpeFamiliarModal()}
        >
          Agregar
        </Button>
      </Box>
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderStyle}>Primer nombre</TableCell>
              <TableCell sx={tableHeaderStyle}>Segundo nombre</TableCell>
              <TableCell sx={tableHeaderStyle}>Primer apellido</TableCell>
              <TableCell sx={tableHeaderStyle}>Segundo apellido</TableCell>
              <TableCell sx={tableHeaderStyle}>Vínculo/Parentesco</TableCell>
              <TableCell sx={tableHeaderStyle}>Nacionalidad</TableCell>
              <TableCell sx={tableHeaderStyle} />
            </TableRow>
          </TableHead>
          <TableBody>
            {ppeFamiliares.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ color: "#7d7d7d" }}>
                  Sin registros.
                </TableCell>
              </TableRow>
            ) : (
              ppeFamiliares.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.primerNombre}</TableCell>
                  <TableCell>{item.segundoNombre}</TableCell>
                  <TableCell>{item.primerApellido}</TableCell>
                  <TableCell>{item.segundoApellido}</TableCell>
                  <TableCell>{item.parentesco}</TableCell>
                  <TableCell>{item.nacionalidad}</TableCell>
                  <TableCell width={72} sx={{ whiteSpace: "nowrap" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenPpeFamiliarModal(item)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setPpeFamiliares((prev) =>
                          prev.filter((row) => row.id !== item.id),
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {ppeFamiliarRequirementError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {ppeFamiliarRequirementError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography sx={{ color: "#3a3a3a", fontSize: 14 }}>
          *** Se aplica la Relación de Empresas si la persona mantiene mas del
          25% de participación de la empresa ***
        </Typography>
        <Button
          startIcon={<AddCircleIcon />}
          variant="outlined"
          onClick={() => handleOpenPpeEmpresaModal()}
        >
          Agregar
        </Button>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderStyle}>Razón Social</TableCell>
              <TableCell sx={tableHeaderStyle}>RTN</TableCell>
              <TableCell sx={tableHeaderStyle}>Giro del Negocio</TableCell>
              <TableCell sx={tableHeaderStyle}>País</TableCell>
              <TableCell sx={tableHeaderStyle} />
            </TableRow>
          </TableHead>
          <TableBody>
            {ppeEmpresas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ color: "#7d7d7d" }}>
                  Sin registros.
                </TableCell>
              </TableRow>
            ) : (
              ppeEmpresas.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.razonSocial}</TableCell>
                  <TableCell>{item.rtn}</TableCell>
                  <TableCell>{item.giroNegocio}</TableCell>
                  <TableCell>{item.pais}</TableCell>
                  <TableCell width={72} sx={{ whiteSpace: "nowrap" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenPpeEmpresaModal(item)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setPpeEmpresas((prev) =>
                          prev.filter((row) => row.id !== item.id),
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openPpeFamiliarModal}
        onClose={handleClosePpeFamiliarModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingPpeFamiliarId !== null
            ? "Editar Familiar"
            : "Agregar Familiar"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.2,
              mt: 0.5,
            }}
          >
            <TextField
              size="small"
              label="Primer Nombre"
              required
              value={ppeFamiliarForm.primerNombre}
              onChange={handlePpeFamiliarChange("primerNombre")}
              error={Boolean(ppeFamiliarError.primerNombre)}
              helperText={ppeFamiliarError.primerNombre || ""}
            />
            <TextField
              size="small"
              label="Segundo Nombre"
              value={ppeFamiliarForm.segundoNombre}
              onChange={handlePpeFamiliarChange("segundoNombre")}
            />
            <TextField
              size="small"
              label="Primer Apellido"
              required
              value={ppeFamiliarForm.primerApellido}
              onChange={handlePpeFamiliarChange("primerApellido")}
              error={Boolean(ppeFamiliarError.primerApellido)}
              helperText={ppeFamiliarError.primerApellido || ""}
            />
            <TextField
              size="small"
              label="Segundo Apellido"
              value={ppeFamiliarForm.segundoApellido}
              onChange={handlePpeFamiliarChange("segundoApellido")}
            />
            <FormControl
              size="small"
              required
              error={Boolean(ppeFamiliarError.parentesco)}
            >
              <InputLabel>Vínculo/Parentesco</InputLabel>
              <Select
                label="Vínculo/Parentesco"
                value={ppeFamiliarForm.parentesco}
                onChange={handlePpeFamiliarChange("parentesco")}
              >
                {ppeParentescoOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              required
              error={Boolean(ppeFamiliarError.nacionalidad)}
            >
              <InputLabel>Nacionalidad</InputLabel>
              <Select
                label="Nacionalidad"
                value={ppeFamiliarForm.nacionalidad}
                onChange={handlePpeFamiliarChange("nacionalidad")}
              >
                {ppeNacionalidadOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePpeFamiliarModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAgregarPpeFamiliar}
            sx={{ color: "#fff" }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPpeEmpresaModal}
        onClose={handleClosePpeEmpresaModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingPpeEmpresaId !== null ? "Editar Empresa" : "Agregar Empresa"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1.2,
              mt: 0.5,
            }}
          >
            <TextField
              size="small"
              label="Razón social"
              required
              value={ppeEmpresaForm.razonSocial}
              onChange={handlePpeEmpresaChange("razonSocial")}
              error={Boolean(ppeEmpresaError.razonSocial)}
              helperText={ppeEmpresaError.razonSocial || ""}
            />
            <TextField
              size="small"
              label="RTN"
              required
              value={ppeEmpresaForm.rtn}
              onChange={handlePpeEmpresaChange("rtn")}
              error={Boolean(ppeEmpresaError.rtn)}
              helperText={ppeEmpresaError.rtn || ""}
            />
            <Autocomplete
              options={giroNegocioCatalogOptions}
              value={ppeEmpresaForm.giroNegocio || null}
              onChange={(_event, value) =>
                handlePpeGiroNegocioSelect(asText(value))
              }
              onInputChange={(_event, _value, reason) => {
                if (reason === "clear") handlePpeGiroNegocioSelect("");
              }}
              disabled={loadingCatalogos}
              noOptionsText="Sin coincidencias"
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Giro del Negocio"
                  required
                  error={Boolean(ppeEmpresaError.giroNegocio)}
                  helperText={ppeEmpresaError.giroNegocio || ""}
                />
              )}
            />
            <FormControl
              size="small"
              required
              error={Boolean(ppeEmpresaError.pais)}
            >
              <InputLabel>País</InputLabel>
              <Select
                label="País"
                value={ppeEmpresaForm.pais}
                onChange={handlePpeEmpresaChange("pais")}
                disabled={loadingCatalogos}
              >
                {paisNacimientoOptions.map((option) => (
                  <MenuItem key={`ppe-pais-${option}`} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePpeEmpresaModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAgregarPpeEmpresa}
            sx={{ color: "#fff" }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );

  const renderTabBeneficiarios = () => (
    <Paper sx={{ ...sectionPaper, pt: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <SectionTitle>
          Listado de Beneficiarios Potenciales y Legales
        </SectionTitle>
        <Button
          startIcon={<AddCircleIcon />}
          variant="outlined"
          onClick={() => handleOpenBeneficiarioModal()}
        >
          Agregar
        </Button>
      </Box>
      <Typography sx={{ mb: 1, color: "#1b5e8f", fontWeight: 600 }}>
        Porcentaje disponible para beneficiarios legales:{" "}
        {porcentajeDisponibleLegal}%
      </Typography>
      <Typography sx={{ mb: 1, color: "#4d4d4d", fontSize: 13 }}>
        Requerido: 1 potencial y legales al 100% (excepto si marca no
        designación legal).
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderStyle}>Primer nombre</TableCell>
              <TableCell sx={tableHeaderStyle}>Segundo nombre</TableCell>
              <TableCell sx={tableHeaderStyle}>Primer apellido</TableCell>
              <TableCell sx={tableHeaderStyle}>Segundo apellido</TableCell>
              <TableCell sx={tableHeaderStyle}>No de Documento</TableCell>
              <TableCell sx={tableHeaderStyle}>Parentesco</TableCell>
              <TableCell sx={tableHeaderStyle}>Porcentaje</TableCell>
              <TableCell sx={tableHeaderStyle}>Tipo</TableCell>
              <TableCell sx={tableHeaderStyle} />
            </TableRow>
          </TableHead>
          <TableBody>
            {beneficiarios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ color: "#7d7d7d" }}>
                  Sin registros.
                </TableCell>
              </TableRow>
            ) : (
              beneficiarios.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.primerNombre}</TableCell>
                  <TableCell>{item.segundoNombre}</TableCell>
                  <TableCell>{item.primerApellido}</TableCell>
                  <TableCell>{item.segundoApellido}</TableCell>
                  <TableCell>
                    {formatIdentificacionVisual(item.numeroDocumento)}
                  </TableCell>
                  <TableCell>{item.parentesco}</TableCell>
                  <TableCell>{item.porcentaje}</TableCell>
                  <TableCell>{item.tipo}</TableCell>
                  <TableCell width={72} sx={{ whiteSpace: "nowrap" }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenBeneficiarioModal(item)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEliminarBeneficiario(item.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {beneficiariosRequirementError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {beneficiariosRequirementError}
        </Alert>
      )}
      <FormControlLabel
        sx={{ mt: 1 }}
        control={
          <Checkbox
            checked={form.noDesignacionBeneficiarios}
            onChange={(event) => {
              const checked = event.target.checked;
              if (checked && totalBeneficiariosLegales > 0) return;
              setForm((prev) => ({
                ...prev,
                noDesignacionBeneficiarios: checked,
              }));
              setBeneficiariosRequirementError("");
            }}
            disabled={totalBeneficiariosLegales > 0}
          />
        }
        label="Marque esta opción si el afiliado no realizará la Designación de Beneficiarios Legales"
      />
      <Dialog
        open={openBeneficiarioModal}
        onClose={handleCloseBeneficiarioModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingBeneficiarioId !== null
            ? "Editar Beneficiario"
            : "Agregar Beneficiario"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 1.2,
              mt: 0.5,
            }}
          >
            <TextField
              size="small"
              required
              error={Boolean(beneficiarioModalError.primerNombre)}
              helperText={beneficiarioModalError.primerNombre || ""}
              label="Primer nombre"
              value={beneficiarioForm.primerNombre}
              onChange={handleBeneficiarioFormChange("primerNombre")}
              onBlur={handleBeneficiarioNameBlur("primerNombre")}
            />
            <TextField
              size="small"
              label="Segundo nombre"
              value={beneficiarioForm.segundoNombre}
              onChange={handleBeneficiarioFormChange("segundoNombre")}
              onBlur={handleBeneficiarioNameBlur("segundoNombre")}
            />
            <TextField
              size="small"
              required
              error={Boolean(beneficiarioModalError.primerApellido)}
              helperText={beneficiarioModalError.primerApellido || ""}
              label="Primer apellido"
              value={beneficiarioForm.primerApellido}
              onChange={handleBeneficiarioFormChange("primerApellido")}
              onBlur={handleBeneficiarioNameBlur("primerApellido")}
            />
            <TextField
              size="small"
              label="Segundo apellido"
              value={beneficiarioForm.segundoApellido}
              onChange={handleBeneficiarioFormChange("segundoApellido")}
              onBlur={handleBeneficiarioNameBlur("segundoApellido")}
            />
            <TextField
              size="small"
              required
              error={Boolean(beneficiarioModalError.numeroDocumento)}
              helperText={beneficiarioModalError.numeroDocumento || ""}
              label="Número de Documento"
              value={formatIdentificacionVisual(
                beneficiarioForm.numeroDocumento,
              )}
              onChange={handleBeneficiarioFormChange("numeroDocumento")}
            />
            <FormControl
              size="small"
              required
              error={Boolean(beneficiarioModalError.parentesco)}
            >
              <InputLabel>Parentesco</InputLabel>
              <Select
                label="Parentesco"
                value={beneficiarioForm.parentesco}
                onChange={handleBeneficiarioFormChange("parentesco")}
              >
                {parentescoOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={beneficiarioForm.esLegal}
                  onChange={handleBeneficiarioFormChange("esLegal")}
                  disabled={form.noDesignacionBeneficiarios}
                />
              }
              label="Beneficiario Legal"
            />
            {beneficiarioModalError.esLegal && (
              <Typography
                variant="caption"
                sx={{ color: "#d32f2f", display: "block" }}
              >
                {beneficiarioModalError.esLegal}
              </Typography>
            )}
          </Box>
          {beneficiarioForm.esLegal && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "220px" },
                gap: 1,
                mt: 0.5,
              }}
            >
              <TextField
                size="small"
                type="number"
                required
                error={Boolean(beneficiarioModalError.porcentaje)}
                helperText={beneficiarioModalError.porcentaje || ""}
                label="Defina un porcentaje"
                value={beneficiarioForm.porcentaje}
                onChange={handleBeneficiarioFormChange("porcentaje")}
              />
            </Box>
          )}
          <Typography sx={{ mt: 1.2, color: "#1b5e8f", fontWeight: 600 }}>
            Porcentaje disponible para asignar: {porcentajeDisponibleLegal}%
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseBeneficiarioModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAgregarBeneficiario}
            sx={{ color: "#fff" }}
          >
            {editingBeneficiarioId !== null ? "Guardar" : "Agregar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );

  const renderTabPerfilLaft = () => (
    <Paper sx={sectionPaper}>
      <SectionTitle>Perfil Laft</SectionTitle>
      <FormControl size="small" sx={{ minWidth: 320, maxWidth: 420 }}>
        <InputLabel>Perfil Laft</InputLabel>
        <Select
          label="Perfil Laft"
          value={form.perfilLaft}
          onChange={handleChange("perfilLaft")}
          disabled={loadingCatalogos}
        >
          {perfilLaftOptions.map((option) => (
            <MenuItem key={`perfil-laft-${option}`} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Paper>
  );

  const renderAffiliationStep = () => (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, newTab) => handleAfiliacionTabChange(newTab)}
        variant="scrollable"
        allowScrollButtonsMobile
        textColor="inherit"
        indicatorColor="primary"
        sx={{
          borderBottom: "1px solid #d9d9d9",
          minHeight: 38,
          "& .MuiTab-root": {
            minHeight: 38,
            textTransform: "none",
            fontSize: 14,
            px: 2,
            minWidth: "fit-content",
          },
          "& .Mui-selected": {
            color: "#0f3e67",
            fontWeight: 700,
          },
          "& .MuiTabs-indicator": {
            display: "none",
          },
        }}
      >
        {afiliacionTabs.map((tabName) => (
          <Tab key={tabName} label={tabName} />
        ))}
      </Tabs>

      <Box sx={{ mt: 1 }}>
        {currentTabName === "Información Laboral" &&
          renderTabInformacionLaboral()}
        {currentTabName === "Patrimonio" && renderTabPatrimonio()}
        {currentTabName === "Referencias" && renderTabReferencias()}
        {currentTabName === "Padecimientos" && renderTabPadecimientos()}
        {currentTabName === "Persona Políticamente Expuesta" && renderTabPpe()}
        {currentTabName === "Beneficiarios" && renderTabBeneficiarios()}
        {currentTabName === "Perfil Laft" && renderTabPerfilLaft()}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.4 },
        background:
          "linear-gradient(180deg, rgba(25,73,140,0.08) 0%, rgba(173,132,241,0.08) 100%)",
        minHeight: "calc(100vh - 90px)",
      }}
    >
      <Paper
        sx={{
          borderRadius: 3,
          border: "1px solid #dbe6f6",
          overflow: "hidden",
          boxShadow: "0 14px 36px rgba(25, 73, 140, 0.12)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #d0d0d0",
            background:
              "linear-gradient(90deg, rgba(25,73,140,0.08) 0%, rgba(173,132,241,0.08) 100%)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "2px solid #666",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#666",
                fontWeight: 700,
              }}
            >
              {step + 1}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#0f3e67",
                  lineHeight: 1.2,
                  fontSize: 28 / 2,
                }}
              >
                {stepTitles[step].title}
              </Typography>
              <Typography variant="body2" sx={{ color: "#555" }}>
                {stepTitles[step].subtitle}
              </Typography>
            </Box>
          </Box>

          {step > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => setStep((prev) => Math.max(prev - 1, 0))}
                disabled={step === 0}
                color="inherit"
                sx={{
                  textTransform: "uppercase",
                  fontWeight: 500,
                  color: step === 0 ? "#b0b0b0" : "#6b6b6b",
                }}
              >
                Anterior
              </Button>
              <Button
                endIcon={<ArrowForwardIcon />}
                onClick={handleNextStep}
                disabled={step === stepTitles.length - 1 || disabledNext}
                color="inherit"
                sx={{
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: "#111",
                }}
              >
                Siguiente
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 2 }}>
          {step === 0 && renderSearchStep()}
          {step === 1 && renderPersonalStep()}
          {step === 2 && renderPhotoStep()}
          {step === 3 && renderAffiliationStep()}
          {step === 4 && renderReviewStep()}
        </Box>

        <Dialog
          open={openPersonaExistenteModal}
          onClose={() => setOpenPersonaExistenteModal(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #d8e2f2",
              boxShadow: "0 18px 40px rgba(25, 73, 140, 0.2)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                background:
                  "linear-gradient(90deg, rgba(25,73,140,0.12) 0%, rgba(173,132,241,0.1) 100%)",
                borderBottom: "1px solid #d8e2f2",
              }}
            >
              <Typography
                sx={{ fontWeight: 600, color: "#1f1f1f", fontSize: 17 }}
              >
                Afiliado ya registrado
              </Typography>
              <Typography sx={{ color: "#44556a", fontSize: 13 }}>
                Este DNI ya existe en el sistema.
              </Typography>
            </Box>

            <Box sx={{ px: 2.4, pb: 2.1, pt: 2 }}>
              <Box
                sx={{
                  width: 112,
                  height: 112,
                  mx: "auto",
                  borderRadius: 1.8,
                  background:
                    "linear-gradient(145deg, rgba(25,73,140,0.95) 0%, rgba(37,95,175,0.92) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  boxShadow: "0 8px 16px rgba(25, 73, 140, 0.2)",
                  overflow: "hidden",
                }}
              >
                {personaExistente?.fotoUrl ? (
                  <img
                    src={personaExistente.fotoUrl}
                    alt="Foto de afiliado"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <AccountCircleIcon sx={{ fontSize: 72 }} />
                )}
              </Box>

              <Box
                sx={{
                  mt: 2,
                  p: 1.6,
                  borderRadius: 2,
                  border: "1px solid #d8e2f2",
                  bgcolor: "#f8fbff",
                  display: "grid",
                  gap: 1.1,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ color: "#19498c", fontWeight: 700 }}>
                    Identificación
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
                    {formatIdentificacionVisual(
                      personaExistente?.identificacion,
                    ) || "-"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ color: "#19498c", fontWeight: 700 }}>
                    Nombres
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
                    {personaExistente?.nombres || "-"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ color: "#19498c", fontWeight: 700 }}>
                    Apellidos
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
                    {personaExistente?.apellidos || "-"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ color: "#19498c", fontWeight: 700 }}>
                    Fecha nacimiento
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
                    {personaExistente?.fechaNacimiento || "-"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ color: "#19498c", fontWeight: 700 }}>
                    Sexo
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: "#1f2937" }}>
                    {personaExistente?.sexo || "-"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              px: 2.2,
              pb: 2,
              pt: 0.2,
              justifyContent: "center",
              borderTop: "1px solid #e6edf8",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setOpenPersonaExistenteModal(false)}
              sx={{
                minWidth: 150,
                color: "#fff",
                fontWeight: 700,
                letterSpacing: 0.2,
                textTransform: "uppercase",
                backgroundColor: "#19498c",
                "&:hover": { backgroundColor: "#143f79" },
              }}
            >
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo para tomar/cambiar fotografía desde el resumen */}
        <Dialog
          open={summaryPhotoOpen}
          onClose={() => handleCloseSummaryPhoto(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CameraAltIcon sx={{ color: "#19498c" }} />
            Tomar / Cambiar Fotografía
          </DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              pt: "12px !important",
            }}
          >
            <Box
              sx={{
                width: 260,
                height: 340,
                border: "1px solid #bdbdbd",
                background: "#111",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
              }}
            >
              {cameraOpen ? (
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : photoDataUrl ? (
                <img
                  src={photoDataUrl}
                  alt="Foto capturada"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <AccountCircleIcon sx={{ fontSize: 120, color: "#555" }} />
              )}
            </Box>
            {cameraError && (
              <Alert severity="error" sx={{ width: "100%", fontSize: 12 }}>
                {cameraError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions
            sx={{ justifyContent: "center", gap: 1, pb: 2, flexWrap: "wrap" }}
          >
            {cameraOpen && (
              <Button
                variant="contained"
                startIcon={<CameraAltIcon />}
                onClick={capturePhoto}
                sx={{
                  backgroundColor: "#19498c",
                  "&:hover": { backgroundColor: "#143f79" },
                }}
              >
                Capturar Foto
              </Button>
            )}
            {!cameraOpen && photoDataUrl && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CameraAltIcon />}
                  onClick={() => {
                    setPhotoDataUrl("");
                    openCamera();
                  }}
                >
                  Volver a tomar
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleCloseSummaryPhoto(true)}
                >
                  Usar esta foto
                </Button>
              </>
            )}
            <Button
              onClick={() => handleCloseSummaryPhoto(false)}
              color="inherit"
            >
              {!cameraOpen && photoDataUrl ? "Cancelar" : "Cerrar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo de registro exitoso */}
        <Dialog open={successOpen} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "success.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 1.5,
              }}
            >
              <Typography sx={{ color: "#fff", fontSize: 36, lineHeight: 1 }}>
                ✓
              </Typography>
            </Box>
            <Typography variant="h6" fontWeight={700}>
              Registro Exitoso
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center", pb: 1 }}>
            <Typography variant="body1" color="text.secondary">
              La afiliación de{" "}
              <strong>{savedPersonaName || "la persona"}</strong> ha sido
              guardada correctamente.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2.5 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleCloseSuccess}
              sx={{ px: 5 }}
            >
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}
